import { mediaRef } from '@/services/mediaAssets';
/* Asset references are colocated with gallery metadata so webpack can bundle each item. */
/* eslint-disable global-require */

import {
  achievementRefs,
  firstRefs,
  tagRefs,
  tripAttendees,
} from './reckonWayTags';

const createTravelBlog = (title) => ({
  eyebrow: 'Travel Blog',
  title: `${title} Travel Blog`,
  lede: 'A longer trip story can live here, from the first day of travel through the best team moments and favorite memories.',
  sections: [
    {
      heading: 'The Journey',
      paragraphs: [
        'Use this section for the arrival, the setting, and the first moments that made the trip feel memorable.',
        'Capture the people, places, inside jokes, and unexpected details that brought the adventure to life.',
      ],
    },
    {
      heading: 'Favorite Moments',
      paragraphs: [
        'Use this section for the highlights: the meals, excursions, conference sessions, celebrations, views, and team moments worth remembering.',
      ],
    },
    {
      heading: 'Looking Back',
      paragraphs: [
        'Use this section for a closing reflection on what made the trip special and why it belongs in the Reckon story.',
      ],
    },
  ],
});

const sxswLondonBlog = {
  eyebrow: 'Travel Blog',
  title: 'SXSW London 2025',
  journey: {
    title: 'The Journey',
    intro: 'This was not just a conference trip. It was a scouting mission to see whether SXSW London could become a future Reckon Crew destination.',
    closingText: 'Every time we thought the day was over, London had one more surprise waiting around the corner.',
    days: [
      {
        dayLabel: 'Monday, June 2',
        title: 'Arrival and Opening Night',
        items: [
          { icon: '✈️', title: 'Arrive in London', linkedSectionId: 'arrival' },
          {
            icon: '🏨',
            title: 'Check In',
            subtitle: 'Nobu London Shoreditch',
            linkedSectionId: 'home-base',
          },
          { icon: '🎟️', title: 'SXSW Badge Pickup', linkedSectionId: 'conference' },
          { icon: '☕', title: 'High Grade Coffee', linkedSectionId: 'high-grade-coffee' },
          {
            icon: '🚶',
            title: 'SXSW London Expo',
            subtitle: 'Getting familiar with the Shoreditch conference layout',
            linkedSectionId: 'walking',
          },
          {
            icon: '🤖',
            title: 'The View from 2050',
            subtitle: 'Truman Brewery, SXSW London Stage',
            linkedSectionId: 'conference',
          },
          {
            icon: '🎵',
            title: 'Henry Lau',
            subtitle: 'Village Underground',
            linkedSectionId: 'henry-lau-village-underground',
          },
          {
            icon: '🍺',
            title: 'The Queen\'s Head',
            subtitle: 'Pub food and pints by the window after Henry Lau',
            linkedSectionId: 'the-queens-head',
          },
          {
            icon: '🎉',
            title: 'SXSW London Opening Party',
            subtitle: 'Truman Brewery, Brick Lane Yard',
            linkedSectionId: 'truman-brewery',
          },
          { icon: '🍺', title: 'Owl & Pussycat', linkedSectionId: 'pubs' },
          {
            icon: '🎶',
            title: 'Old Street Records',
            subtitle: 'Live music to close out the night',
            linkedSectionId: 'old-street-records',
          },
        ],
      },
      {
        dayLabel: 'Tuesday, June 3',
        title: 'Engineering the Future and Film',
        items: [
          { icon: '☕', title: 'High Grade Coffee', linkedSectionId: 'high-grade-coffee' },
          { icon: '🐟', title: 'Shoreditch Fish & Chips', linkedSectionId: 'shoreditch-fish-and-chips' },
          {
            icon: '🚫', title: 'Building AI Products', subtitle: 'Missed after queueing at Shoreditch Electric', linkedSectionId: 'conference',
          },
          {
            icon: '🤖',
            title: 'Rethinking Robotics',
            subtitle: 'Attended the beginning at Shoreditch Electric',
            linkedSectionId: 'conference',
          },
          {
            icon: '🍺',
            title: 'The Reliance',
            subtitle: 'Met Marcus and ate before walking toward Curzon Hoxton',
            linkedSectionId: 'the-reliance',
          },
          {
            icon: '🍺',
            title: 'George & Vulture',
            subtitle: 'A pint outside before the film premiere',
            linkedSectionId: 'george-and-vulture',
          },
          {
            icon: '🎬',
            title: 'Late Shift Premiere',
            subtitle: 'Curzon Hoxton',
            linkedSectionId: 'late-shift-curzon-hoxton',
          },
          {
            icon: '🎙️',
            title: 'Leonie Benesch Q&A',
            subtitle: 'She had been sitting in our row without us realizing it',
            linkedSectionId: 'late-shift-curzon-hoxton',
          },
        ],
      },
      {
        dayLabel: 'Wednesday, June 4',
        title: 'AI, Hollywood, and Music',
        items: [
          { icon: '☕', title: 'High Grade Coffee', linkedSectionId: 'high-grade-coffee' },
          {
            icon: '🥯',
            title: 'Beigel Bake',
            subtitle: 'Salt beef bagel on Brick Lane',
            linkedSectionId: 'beigel-bake',
          },
          { icon: '🎤', title: 'The AI Voice Revolution', linkedSectionId: 'ai-software' },
          { icon: '🍺', title: 'The Golden Heart', linkedSectionId: 'pubs' },
          { icon: '🎤', title: 'AI Agents: The Marketer’s New Best Friend?', linkedSectionId: 'ai-software' },
          {
            icon: '🚫',
            title: 'Creativity as Capital for Change',
            subtitle: 'Tried to see Idris Elba, but the queue stretched for blocks',
            linkedSectionId: 'conference',
          },
          { icon: '🍺', title: 'The Old Blue Last', linkedSectionId: 'the-old-blue-last' },
          {
            icon: '🎬',
            title: 'Deep Cover World Premiere',
            subtitle: 'Barbican Centre',
            linkedSectionId: 'deep-cover-barbican-centre',
          },
          {
            icon: '🎙️',
            title: 'Cast Q&A',
            subtitle: 'Orlando Bloom, Bryce Dallas Howard, and Nick Mohammed',
            linkedSectionId: 'deep-cover-barbican-centre',
          },
          { icon: '🚶', title: 'Walk to Hoxton Hall', linkedSectionId: 'walking' },
          {
            icon: '🎸',
            title: 'The K\'s',
            subtitle: 'Hoxton Hall',
            linkedSectionId: 'music',
          },
          { icon: '🚶', title: 'Walk to XOYO', linkedSectionId: 'walking' },
          { icon: '😂', title: 'Marcus Almost Walks Into Idris Elba', linkedSectionId: 'xoyo-idris-elba-dj-set' },
          { icon: '🎤', title: 'Wyclef Jean Walks By', linkedSectionId: 'xoyo-idris-elba-dj-set' },
          {
            icon: '🎧',
            title: 'Idris Elba DJ Set',
            subtitle: 'XOYO after midnight',
            linkedSectionId: 'xoyo-idris-elba-dj-set',
          },
          {
            icon: '🌯',
            title: 'Hunger Cure',
            subtitle: 'Late-night kebab before heading back to the hotel',
            linkedSectionId: 'hunger-cure',
          },
        ],
      },
      {
        dayLabel: 'Thursday, June 5',
        title: 'One Last London Adventure',
        items: [
          { icon: '☕', title: 'High Grade Coffee', linkedSectionId: 'high-grade-coffee' },
          { icon: '🎤', title: 'Agentic AI & The People Powered Future', linkedSectionId: 'conference' },
          {
            icon: '🍺', title: 'The Reliance', subtitle: 'Second visit', linkedSectionId: 'the-reliance',
          },
          { icon: '🏰', title: 'Tower of London', linkedSectionId: 'tower-bridge-to-bermondsey-beer-mile' },
          { icon: '🌉', title: 'Cross Tower Bridge', linkedSectionId: 'tower-bridge-to-bermondsey-beer-mile' },
          { icon: '🍺', title: 'Bermondsey Beer Mile', linkedSectionId: 'tower-bridge-to-bermondsey-beer-mile' },
          { icon: '🍺', title: 'Doodle Bar', linkedSectionId: 'tower-bridge-to-bermondsey-beer-mile' },
          {
            icon: '🍺',
            title: 'The Barrel Project',
            subtitle: 'Favorite brewery of the trip',
            linkedSectionId: 'the-barrel-project',
          },
          { icon: '🍺', title: 'Fabal Beerhall', linkedSectionId: 'tower-bridge-to-bermondsey-beer-mile' },
          { icon: '🍯', title: 'Hiver Taproom', linkedSectionId: 'tower-bridge-to-bermondsey-beer-mile' },
          { icon: '🚶', title: 'Walk Back Along Druid Street', linkedSectionId: 'tower-bridge-to-bermondsey-beer-mile' },
          {
            icon: '🍗',
            title: 'Poulet',
            subtitle: 'The best meal we had in London',
            linkedSectionId: 'poulet',
          },
          {
            icon: '🍺',
            title: 'The Culpeper',
            subtitle: 'Final pint before returning to Nobu',
            linkedSectionId: 'the-culpeper',
          },
        ],
      },
    ],
  },
  lede: 'Every year there are hundreds of technology conferences around the world. We chose SXSW London because it promised something different.',
  sections: [
    {
      heading: 'At a Glance',
      paragraphs: [],
      rows: [
        { label: 'Destination', value: 'London, United Kingdom' },
        { label: 'Dates', value: 'June 2–7, 2025' },
        { label: 'Crew Members', value: '2' },
        { label: 'Conference', value: 'SXSW London' },
        { label: 'Trip Type', value: 'Conference' },
        { label: 'Home Base', value: 'Nobu London Shoreditch' },
        { label: 'Time Zones Crossed', value: '5' },
        { label: 'Films Premiered', value: '2' },
        { label: 'Live Music Events', value: '4' },
        { label: 'Breweries Visited', value: '4' },
        { label: 'Pubs Visited', value: '10+' },
        { label: 'Family Friendly', value: 'No' },
      ],
    },
    {
      heading: 'Why SXSW London',
      paragraphs: [
        'Instead of being confined to a convention center, the conference spread throughout Shoreditch, transforming theaters, music venues, warehouses, pubs, and courtyards into places where ideas, creativity, and innovation intersected.',
        'With just two of us on the trip, this was also a scouting mission. Could SXSW London work for a larger Reckon Crew in the future?',
        'By the end of the week, the answer was easy.',
        'Absolutely.',
      ],
      calloutLabel: 'Purpose',
      callout: 'Attend the inaugural SXSW London conference to explore emerging trends in AI, software development, film, and music while evaluating the event as a potential future Reckon Crew destination.',
    },
    {
      heading: 'Arrival in London',
      id: 'arrival',
      paragraphs: [
        'London immediately set the tone for the week: layered, busy, creative, and best understood on foot. Shoreditch gave the trip a home base with enough restaurants, pubs, coffee, music, and street-level energy to make every gap between conference sessions feel useful.',
        'That mattered. If this was going to work as a future Reckon Crew destination, the city needed to offer more than scheduled programming. It needed to reward curiosity between the official events.',
      ],
    },
    {
      heading: 'Home Base',
      id: 'home-base',
      paragraphs: [
        'Nobu London Shoreditch put us close to the center of the action without making the trip feel boxed into the conference. From there, the week became a pattern of sessions, long walks, nearby dinners, and late conversations about whether London had the right ingredients for a larger team adventure.',
      ],
    },
    {
      heading: 'High Grade Coffee',
      id: 'high-grade-coffee',
      paragraphs: [
        'Every conference needs a good coffee shop, and for us it was High Grade Coffee.',
        'Conveniently located near the former Truman Brewery complex, it quickly became part of our daily rhythm before heading into SXSW sessions.',
      ],
    },
    {
      heading: 'Conference Highlights',
      id: 'conference',
      paragraphs: [
        'SXSW London blended technology, film, music, business, and culture into a city-wide festival. Rather than spending the week inside a single convention center, we moved between venues, neighborhoods, theaters, pubs, and performance spaces throughout Shoreditch.',
      ],
      subsections: [
        {
          heading: 'Truman Brewery',
          id: 'truman-brewery',
          paragraphs: [
            'Despite the name, The Truman Brewery was no longer operating as a brewery. The former brewery complex felt like the heart of SXSW London.',
            'The site included multiple enclosed session spaces, stages, lounges, food trucks, and outdoor areas where attendees could watch major sessions on large screens when indoor venues reached capacity.',
            'It was a conference hub, gathering place, food court, outdoor lounge, and networking space all at once.',
            'The layout made SXSW London feel less like a traditional conference and more like a creative festival spread across Shoreditch.',
          ],
        },
        {
          heading: 'Monday: The View from 2050',
          paragraphs: [
            'We attended The View from 2050 at the Truman Brewery after visiting the SXSW London Expo.',
          ],
        },
        {
          heading: 'Tuesday: Shoreditch Electric',
          paragraphs: [
            'We queued for Building AI Products but could not get inside in time. We attended the beginning of Rethinking Robotics, then left early to meet at The Reliance.',
          ],
        },
        {
          heading: 'Wednesday: The AI Voice Revolution',
          rows: [
            { label: 'Speaker', value: 'Mati Staniszewski' },
            { label: 'Moderator', value: 'Jeremy Kahn' },
          ],
          paragraphs: [
            'This session focused on the rapid evolution of AI-generated voice, speech synthesis, accessibility, localization, and how people will interact with software in the future.',
          ],
        },
        {
          heading: 'The Session We Could Not Get Into',
          paragraphs: [
            'One of the sessions we were most excited about was Creativity as Capital for Change with Idris Elba.',
            'Unfortunately, so was everyone else.',
            'The queue stretched for multiple blocks, and even with Platinum badges, we could not get inside. Missing the session was disappointing, but it set up one of the best stories of the trip later that night at XOYO.',
          ],
        },
      ],
    },
    {
      heading: 'AI and Software Development',
      id: 'ai-software',
      paragraphs: [
        'The technology track gave us a chance to hear how teams, creators, and founders were thinking about AI and software development in real time. The most useful takeaways were less about any single tool and more about the pace of change, the questions people were asking, and where the industry conversation seemed to be heading.',
        'Those conversations carried into meals and walks afterward, which is usually where the best reflection happens anyway.',
      ],
    },
    {
      heading: 'Film',
      id: 'film',
      paragraphs: [
        'Two film premieres gave the trip a different kind of creative energy. They broke up the conference schedule and made SXSW London feel broader than a traditional industry event.',
        'That mix is part of what made the conference interesting. A future Reckon Crew visit would not have to be only about technology. It could include storytelling, music, food, neighborhoods, and the creative culture around the event.',
      ],
      subsections: [
        {
          heading: 'Late Shift at Curzon Hoxton',
          id: 'late-shift-curzon-hoxton',
          paragraphs: [
            'Curzon Hoxton hosted the premiere of Late Shift.',
            'The setting was intimate, and the most memorable moment came after the credits rolled. Actress Leonie Benesch stood up from our row to participate in the audience Q&A.',
            'We had no idea she had been sitting just a few seats away from us during the entire film.',
            'It was one of those uniquely SXSW moments where the distance between audience and creator disappeared.',
          ],
        },
        {
          heading: 'Deep Cover at the Barbican Centre',
          id: 'deep-cover-barbican-centre',
          paragraphs: [
            'The Barbican Centre hosted the world premiere of Deep Cover.',
            'Before the movie even started, we stood outside watching the red carpet arrivals. Camera flashes lit up the entrance as Orlando Bloom, Bryce Dallas Howard, Nick Mohammed, and others arrived.',
            'It felt like we had stepped into Hollywood for the evening.',
            'After the film, the experience continued with a live Q&A featuring Orlando Bloom, Bryce Dallas Howard, and Nick Mohammed.',
          ],
        },
      ],
    },
    {
      heading: 'Music',
      id: 'music',
      paragraphs: [
        'Live music became one of the strongest arguments for London as a future destination. Four music events across the week gave the trip movement and texture after the formal programming ended.',
        'The music was not a side note. It was part of how the city opened up, one venue and one neighborhood at a time.',
      ],
      subsections: [
        {
          heading: 'Henry Lau at Village Underground',
          id: 'henry-lau-village-underground',
          paragraphs: [
            'We saw Henry Lau perform at Village Underground.',
            'The queue wrapped around the block. We joined it around 1:45 or 2:00 PM and waited at least 90 minutes before entering around 3:00 PM for the 4:00 PM performance.',
            'The crowd felt completely different from what we were used to, almost like what I imagine a K-pop performance would feel like. I was not familiar with Henry Lau before the show, but I left impressed by his talent, musicianship, and showmanship.',
          ],
        },
        {
          heading: 'Old Street Records',
          id: 'old-street-records',
          paragraphs: [
            'Old Street Records is where we closed out one of the early nights with live music.',
            'After Henry Lau at Village Underground, we were not quite ready to head back to the hotel. Old Street Records gave us one more round of live music before calling it a night.',
          ],
        },
        {
          heading: 'Idris Elba at XOYO',
          id: 'xoyo-idris-elba-dj-set',
          paragraphs: [
            'XOYO became one of the most memorable stops of the entire trip.',
            'Earlier in the day, we had tried to get into Idris Elba\'s session but could not because the queue stretched for blocks. Later that night, we headed to XOYO to see him DJ after midnight.',
            'While standing outside XOYO trying to figure out which queue our Platinum badges would get us into, Marcus nearly walked directly into Idris Elba and his entourage.',
            'Focused on the logistics, he stepped aside and kept moving without realizing who he had almost run into.',
            'Moments later, Wyclef Jean walked by.',
            'Marcus somehow missed him too.',
            'For the rest of the trip, we joked that Marcus had managed to stand within arm\'s reach of two global music icons and miss both of them.',
            'Inside, Idris Elba\'s DJ set became the perfect ending to one of the most memorable SXSW days.',
          ],
        },
      ],
    },
    {
      heading: 'Food Stops',
      id: 'food',
      paragraphs: [
        'The best conference days had food built into the route: quick lunches, late-night saves, and meals that gave us a chance to reset before the next session or venue.',
      ],
      subsections: [
        {
          heading: 'Shoreditch Fish & Chips',
          id: 'shoreditch-fish-and-chips',
          paragraphs: [
            'After a morning session, we walked to Shoreditch Fish & Chips for a classic London lunch.',
            'The fish was amazingly fresh and amazingly hot. So hot that the first bite burned the roof of my mouth.',
            'Lesson learned: give it a few minutes before digging in.',
            'We also tried mushy peas, because when in London, you try the mushy peas.',
          ],
        },
        {
          heading: 'Beigel Bake',
          id: 'beigel-bake',
          paragraphs: [
            'We stopped at Beigel Bake around lunch on Wednesday.',
            'The line stretched out the door, which was the only reason I went there in the first place. When nearly everyone ahead of us ordered the salt beef bagel, I followed their lead.',
            'No regrets.',
            'It became one of those meals I still think about longingly whenever I eat a Reuben back home.',
          ],
        },
        {
          heading: 'Hunger Cure',
          id: 'hunger-cure',
          paragraphs: [
            'After the Idris Elba DJ set at XOYO, we stopped for a late-night kebab at Hunger Cure before heading back to the hotel.',
            'It was the right meal at exactly the right time after one of the longest and most memorable days of the trip.',
          ],
        },
        {
          heading: 'Poulet',
          id: 'poulet',
          paragraphs: [
            'We spent so much time enjoying the Bermondsey Beer Mile that we completely forgot to eat dinner.',
            'By the time we started walking back, most restaurants were closing.',
            'Then Poulet had one chicken left.',
            'Ten minutes later, it became the best meal we had in London.',
          ],
        },
      ],
    },
    {
      heading: 'Pubs and Breweries',
      id: 'pubs',
      paragraphs: [
        'The pub count crossed into double digits quickly, and four brewery stops gave the week an easy rhythm outside the conference schedule. Some places were planned. Others happened because a walk took a turn, a sign looked promising, or the next conversation needed a better setting.',
        'For a conference trip, that matters. A good Reckon Crew destination needs reliable places to gather without over-planning every hour. London had no shortage of them.',
      ],
      subsections: [
        {
          heading: 'The Queen\'s Head',
          id: 'the-queens-head',
          paragraphs: [
            'After Henry Lau, we stopped at The Queen\'s Head for a pint and some pub food before the Festival Opening Party.',
            'We grabbed the empty stools by the front window so we could people watch while enjoying a pint.',
            'It was the perfect way to slow down and reset between Henry Lau and the Festival Opening Party.',
          ],
        },
        {
          heading: 'George & Vulture',
          id: 'george-and-vulture',
          paragraphs: [
            'The George & Vulture was next to Curzon Hoxton, where we saw the premiere of Late Shift.',
            'We stopped in for a pint before the movie. The inside was a beautiful old pub, but the weather was too good to ignore, so we sat outside at the picnic tables.',
            'It gave us the perfect chance to enjoy the weather, people watch, and settle in before the film.',
          ],
        },
        {
          heading: 'The Reliance',
          id: 'the-reliance',
          paragraphs: [
            'The Reliance became our unofficial neighborhood pub.',
            'It was conveniently located on the walk between venues, so Marcus and I used it as an impromptu meeting place whenever we split up to attend different sessions.',
            'Marcus loved the Wagyu Beef Burger so much that we visited twice.',
            'By the end of the week, The Reliance felt less like a random pub and more like our London meeting spot.',
          ],
        },
        {
          heading: 'The Old Blue Last',
          id: 'the-old-blue-last',
          paragraphs: [
            'We went to The Old Blue Last after failing to get into Idris Elba’s session.',
            'It was the stop before Deep Cover at the Barbican.',
          ],
        },
        {
          heading: 'The Barrel Project',
          id: 'the-barrel-project',
          paragraphs: [
            'The Barrel Project was probably my favorite stop on the Beer Mile.',
            'The atmosphere made it memorable immediately. Sitting among walls lined with aging barrels of beer gave the space a completely different feel from the other breweries.',
            'It felt like the kind of place you only find by walking a little farther than planned.',
          ],
        },
        {
          heading: 'The Culpeper',
          id: 'the-culpeper',
          paragraphs: [
            'The Culpeper was our final pint after Poulet.',
            'We stayed until last call, near the midnight boundary.',
          ],
        },
      ],
    },
    {
      heading: 'Walking the City',
      id: 'walking',
      paragraphs: [
        'More than 40 miles of walking turned London into the real agenda. Shoreditch, conference venues, pubs, restaurants, music events, and side streets all connected into one long route through the city.',
        'The walking was not just transportation. It was how the trip found its shape.',
      ],
      subsections: [
        {
          heading: 'Tower Bridge to Bermondsey Beer Mile',
          id: 'tower-bridge-to-bermondsey-beer-mile',
          paragraphs: [
            'On Thursday, after morning SXSW sessions, we decided to wrap up the London portion of the trip by walking to the Tower of London, crossing Tower Bridge, and continuing toward the Bermondsey Beer Mile.',
            'It was surprisingly quiet, but that made the afternoon even better.',
            'The breweries and beer halls were tucked into railway arches and side streets, giving the area a relaxed, local feel. We walked west to east, stopping along the way and enjoying the fact that we had found another side of London beyond the conference venues.',
            'Bermondsey Beer Mile stops:',
          ],
          bullets: [
            'Doodle Bar',
            'The Barrel Project',
            'Fabal Beerhall',
            'Hiver Taproom',
          ],
        },
      ],
    },
    {
      heading: 'Scouting Notes',
      id: 'scouting',
      paragraphs: [
        'The big question was whether SXSW London could work for a future Reckon Crew trip. By the end of the week, the answer felt promising.',
        'The conference had enough substance. The city had enough variety. Shoreditch made a strong home base. The food, pubs, breweries, live music, film, and walkability all added layers that would make the trip feel bigger than the event itself.',
      ],
      calloutLabel: 'Scouting Takeaway',
      callout: 'London has the structure of a conference trip and the soul of a city adventure.',
    },
    {
      heading: 'Scout\'s Report',
      id: 'scouts-report',
      paragraphs: [],
      rows: [
        { label: 'Verdict', value: 'Reckon Approved' },
        { label: 'Would Bring the Crew', value: 'Yes' },
        { label: 'Ideal Crew Size', value: '8-15' },
      ],
      subsections: [
        {
          heading: 'What Worked',
          bullets: [
            'Shoreditch was a strong home base',
            'SXSW venues were walkable',
            'Platinum badges helped with high-demand events',
            'The conference blended AI, technology, film, music, and culture',
            'Excellent food and pub options between sessions',
            'Easy to split up for sessions and regroup afterward',
            'Great potential for future Reckon Crew experiences',
          ],
        },
        {
          heading: 'Considerations',
          bullets: [
            'Popular sessions can reach capacity quickly',
            'Comfortable walking shoes are required',
            'Build in extra time between venues',
            'Have backup plans for high-demand events',
            'Leave room for spontaneous discoveries',
            'Use a convenient pub or coffee shop as a meeting point',
          ],
        },
      ],
      calloutLabel: 'Final Thought',
      callout: 'SXSW London is not just a conference. It is a city-wide festival that rewards curiosity, flexibility, and a willingness to keep walking.',
    },
    {
      heading: 'Favorite Things',
      id: 'favorite-things',
      paragraphs: [],
      rows: [
        { label: 'Best Meal', value: 'Poulet' },
        { label: 'Best Pub', value: 'The Reliance' },
        { label: 'Best Brewery', value: 'The Barrel Project' },
        { label: 'Best Conference Hub', value: 'Truman Brewery' },
        { label: 'Best Film Experience', value: 'Deep Cover World Premiere' },
        { label: 'Best Music Experience', value: 'Idris Elba at XOYO' },
        { label: 'Best Walk', value: 'Tower of London to Tower Bridge to Bermondsey Beer Mile' },
        { label: 'Best Unexpected Moment', value: 'Marcus almost walking into Idris Elba outside XOYO' },
        { label: 'Best Quick Lunch', value: 'Beigel Bake' },
        { label: 'Best Classic London Lunch', value: 'Shoreditch Fish & Chips' },
      ],
    },
    {
      heading: 'The Reckon Crew',
      paragraphs: [],
      rows: [
        { label: 'Kevin L.', value: 'CEO', route: '/passports/kevin-l' },
        { label: 'Marcus G.', value: 'Co-Founder', route: '/passports/marcus-g' },
      ],
    },
    {
      heading: 'Looking Back',
      paragraphs: [
        'SXSW London gave us exactly what we hoped for and more than we expected.',
        'It delivered serious conversations about AI, software, biotechnology, and the future of creativity. But it also delivered film premieres, live music, red carpets, historic pubs, late-night kebabs, hidden breweries, and one unforgettable rotisserie chicken.',
        'Most importantly, it answered the question we came to explore.',
        'Could London work for a future Reckon Crew adventure?',
        'Absolutely.',
      ],
      emphasis: 'That\'s the Reckon Way.',
    },
  ],
};

const sxswLondonSectionByHeading = sxswLondonBlog.sections.reduce((sections, section) => ({
  ...sections,
  [section.heading]: section,
}), {});

const sxswLondonSubsection = (sectionHeading, subsectionHeading) => {
  const section = sxswLondonSectionByHeading[sectionHeading];

  if (!section || !section.subsections) {
    return null;
  }

  return section.subsections.find((subsection) => subsection.heading === subsectionHeading) || null;
};

const sxswLondonConferenceHighlights = {
  ...sxswLondonSectionByHeading['Conference Highlights'],
  subsections: sxswLondonSectionByHeading['Conference Highlights'].subsections.filter(
    (subsection) => subsection.heading !== 'Truman Brewery',
  ),
};

sxswLondonBlog.sections = [
  {
    heading: 'Trip Summary',
    id: 'trip-summary',
    paragraphs: [
      'Instead of being confined to a convention center, the conference spread throughout Shoreditch, transforming theaters, music venues, warehouses, pubs, and courtyards into places where ideas, creativity, and innovation intersected.',
      'With just two of us on the trip, this was also a scouting mission. Could SXSW London work for a larger Reckon Crew in the future?',
      'By the end of the week, the answer was easy.',
      'Absolutely.',
    ],
    calloutLabel: 'Purpose',
    callout: 'Attend the inaugural SXSW London conference to explore emerging trends in AI, software development, film, and music while evaluating the event as a potential future Reckon Crew destination.',
    subsections: [
      sxswLondonSectionByHeading['Arrival in London'],
      sxswLondonSectionByHeading['Home Base'],
    ].filter(Boolean),
  },
  {
    ...sxswLondonSectionByHeading['The Reckon Crew'],
    id: 'reckon-crew',
  },
  sxswLondonSectionByHeading['Scout\'s Report'],
  {
    heading: 'The Journey Timeline',
    id: 'journey-timeline',
    paragraphs: [
      sxswLondonBlog.journey.intro,
      'The floating journey rail follows the trip chronologically so readers can jump from arrival to sessions, films, music, pubs, the Bermondsey Beer Mile, Poulet, and the final walk back to Shoreditch.',
    ],
    rows: sxswLondonBlog.journey.days.map((day) => ({
      label: day.dayLabel,
      value: day.title,
    })),
  },
  sxswLondonConferenceHighlights,
  {
    heading: 'Featured SXSW Venues',
    id: 'featured-sxsw-venues',
    paragraphs: [
      'The strength of SXSW London was the way the venues turned Shoreditch into the conference floor. The best moments happened across breweries, theaters, music clubs, pubs, and creative spaces rather than inside one convention center.',
    ],
    subsections: [
      sxswLondonSubsection('Conference Highlights', 'Truman Brewery'),
      {
        heading: 'Nobu London Shoreditch',
        id: 'nobu-london-shoreditch',
        paragraphs: [
          'Nobu London Shoreditch put us close to the center of the action without making the trip feel boxed into the conference. From there, the week became a pattern of sessions, long walks, nearby dinners, and late conversations about whether London had the right ingredients for a larger team adventure.',
        ],
      },
      sxswLondonSubsection('Music', 'Henry Lau at Village Underground'),
      sxswLondonSubsection('Film', 'Late Shift at Curzon Hoxton'),
      sxswLondonSubsection('Film', 'Deep Cover at the Barbican Centre'),
      sxswLondonSubsection('Music', 'Idris Elba at XOYO'),
      sxswLondonSubsection('Music', 'Old Street Records'),
    ].filter(Boolean),
  },
  {
    heading: 'Food and Drink Along the Way',
    id: 'food-and-drink',
    paragraphs: [
      'The best conference days had food and drink built into the route: coffee before sessions, quick lunches between venues, pubs for regrouping, brewery stops after long walks, and late-night saves when the day ran longer than planned.',
    ],
    subsections: [
      sxswLondonSectionByHeading['High Grade Coffee'],
      sxswLondonSubsection('Food Stops', 'Shoreditch Fish & Chips'),
      sxswLondonSubsection('Food Stops', 'Beigel Bake'),
      sxswLondonSubsection('Pubs and Breweries', 'The Queen\'s Head'),
      sxswLondonSubsection('Pubs and Breweries', 'George & Vulture'),
      sxswLondonSubsection('Pubs and Breweries', 'The Reliance'),
      sxswLondonSubsection('Pubs and Breweries', 'The Old Blue Last'),
      sxswLondonSubsection('Walking the City', 'Tower Bridge to Bermondsey Beer Mile'),
      sxswLondonSubsection('Pubs and Breweries', 'The Barrel Project'),
      sxswLondonSubsection('Food Stops', 'Poulet'),
      sxswLondonSubsection('Pubs and Breweries', 'The Culpeper'),
      sxswLondonSubsection('Food Stops', 'Hunger Cure'),
    ].filter(Boolean),
  },
  {
    heading: 'Only at Reckon',
    id: 'only-at-reckon',
    paragraphs: [
      'Some of the best moments from this conference were the ones no itinerary could have planned.',
    ],
    subsections: [
      {
        heading: 'Two Icons, Zero Recognition',
        paragraphs: [
          'While standing outside XOYO trying to figure out which queue our Platinum badges would get us into, Marcus nearly walked directly into Idris Elba and his entourage.',
          'Focused on the logistics, he stepped aside and kept moving without realizing who he had almost run into.',
          'Moments later, Wyclef Jean walked by.',
          'Marcus somehow missed him too.',
          'For the rest of the trip, we joked that Marcus had managed to stand within arm\'s reach of two global music icons and miss both of them.',
        ],
      },
      {
        heading: 'One Chicken Left',
        paragraphs: [
          'We spent so much time enjoying the Bermondsey Beer Mile that we completely forgot to eat dinner.',
          'By the time we started walking back, most restaurants were closing.',
          'Then Poulet had one chicken left.',
          'Ten minutes later, it became the best meal we had in London.',
        ],
      },
    ],
  },
  sxswLondonSectionByHeading['Favorite Things'],
  {
    heading: 'Merit Badges',
    id: 'merit-badges',
    paragraphs: [
      'The London conference earned a mix of culture, food and drink, and walking adventure badges.',
    ],
    rows: [
      { label: 'Bermondsey Beer Mile', value: 'Food & Drink' },
      { label: 'Guinness Storehouse and Temple Bar', value: 'Food & Drink' },
    ],
  },
  {
    heading: 'Photo Gallery',
    id: 'photo-gallery-story',
    paragraphs: [
      'Photos from SXSW London will live in the gallery below as the page grows, covering conference venues, film premieres, pubs, breweries, walks, meals, and the moments between the itinerary.',
    ],
  },
  {
    ...sxswLondonSectionByHeading['Looking Back'],
    heading: 'Closing Reflection',
    id: 'closing-reflection',
  },
].filter(Boolean);

const sxswLondonJourneyLinkAliases = {
  'ai-software': 'conference',
  music: 'featured-sxsw-venues',
  pubs: 'food-and-drink',
  walking: 'journey-timeline',
};

sxswLondonBlog.journey.days = sxswLondonBlog.journey.days.map((day) => ({
  ...day,
  items: day.items.map((item) => ({
    ...item,
    linkedSectionId: sxswLondonJourneyLinkAliases[item.linkedSectionId]
      || item.linkedSectionId,
  })),
}));

const sxswSydneyBlog = {
  eyebrow: 'Travel Blog',
  title: 'SXSW Sydney 2025',
  journey: {
    title: 'The Journey',
    intro: "The conference brought us to Sydney. Here's how we experienced the city.",
    closingText: 'Every stop became part of the story. Some were planned, others were discovered along the way, but together they made Sydney unforgettable.',
    items: [
      { icon: '✈️', title: 'Depart Washington, D.C.' },
      { icon: '🛬', title: 'Arrive in Sydney' },
      {
        icon: '🏨',
        title: 'Check In',
        subtitle: 'W Sydney - Darling Harbour',
        linkedSectionId: 'check-in',
      },
      {
        icon: '🌊',
        title: 'Coogee to Bondi Coastal Walk',
        subtitle: 'Harry\'s Soft Serve, Sculpture by the Sea, and Bondi Beach.',
        linkedSectionId: 'bondi-to-coogee',
        memory: {
          type: 'unexpected',
          title: 'Sculpture by the Sea',
          text: 'We happened to visit while Sydney\'s annual Sculpture by the Sea exhibition was on display, turning the Coogee-to-Bondi walk into an outdoor art gallery.',
        },
      },
      {
        icon: '03',
        title: 'Bondi Trattoria',
        subtitle: 'The first team meal of the trip.',
        linkedSectionId: 'bondi-trattoria',
      },
      {
        icon: '🎤',
        title: 'SXSW Sydney Begins',
        linkedSectionId: 'conference',
      },
      { icon: '🍽️', title: 'Quay', linkedSectionId: 'quay' },
      { icon: '🚶', title: 'Evening Walk Through The Rocks', linkedSectionId: 'quay' },
      { icon: '🍺', title: 'Glenmore Hotel', linkedSectionId: 'glenmore-hotel' },
      { icon: '🍺', title: 'Fortune of War Hotel', linkedSectionId: 'fortune-of-war-hotel' },
      {
        icon: '🍕',
        title: 'Australian Heritage Hotel',
        subtitle: 'Coat of Arms Pizza.',
        linkedSectionId: 'coat-of-arms-pizza',
      },
      { icon: '🏨', title: 'Walk Back to W Sydney', linkedSectionId: 'walk-back-to-w-sydney' },
      {
        icon: '🥟',
        title: 'Nine Dragons',
        subtitle: 'Lunch and most of the team\'s first dim sum experience.',
        linkedSectionId: 'nine-dragons',
      },
      {
        icon: '🍺',
        title: 'Marrickville Hotel',
        subtitle: 'Pre-dinner drinks before walking to 20 Chapel.',
        linkedSectionId: 'marrickville-hotel',
      },
      { icon: '🚶', title: 'Walk to 20 Chapel', linkedSectionId: 'walk-to-20-chapel' },
      {
        icon: '🍸',
        title: '20 Chapel',
        subtitle: 'The unanimous group favorite.',
        linkedSectionId: '20-chapel',
      },
      { icon: '🚢', title: 'Ferry to Manly', linkedSectionId: 'ferry-to-manly' },
      { icon: '🍺', title: '4 Pines Brew Pub', linkedSectionId: '4-pines' },
      { icon: '🚠', title: 'Blue Mountains', linkedSectionId: 'blue-mountains' },
      {
        icon: '🍺',
        title: 'Mountain Culture Beer Co.',
        subtitle: 'A stop on the way back to Sydney that we wish could have lasted longer.',
        linkedSectionId: 'mountain-culture',
      },
      { icon: '🍸', title: 'Bar Torino', linkedSectionId: 'bar-torino' },
      { icon: '🥩', title: 'Margaret', linkedSectionId: 'margaret' },
      { icon: '🍺', title: 'Stitch Bar', linkedSectionId: 'stitch-bar' },
      { icon: '✈️', title: 'Home' },
    ],
  },
  lede: 'Sydney, Australia | October 13–19, 2025. SXSW brought us to Sydney. The city made the trip unforgettable.',
  sections: [
    {
      heading: 'At a Glance',
      paragraphs: [],
      rows: [
        { label: '📍 Destination', value: 'Sydney, Australia' },
        { label: '🏨 Home Base', value: 'W Sydney - Darling Harbour' },
        { label: '📅 Dates', value: 'October 13–19, 2025' },
        { label: '👥 Reckon Crew Members', value: '6' },
        { label: '🎤 Conference', value: 'SXSW Sydney' },
        { label: '🌍 Time Zones Crossed', value: '15' },
        { label: '🍽️ Team Dinners', value: '4' },
        { label: '🍺 Brewery Visits', value: '1' },
        { label: '🍻 Pubs Visited', value: 'Too many to count' },
        { label: '🎵 Live Concerts', value: 'Every afternoon' },
      ],
    },
    {
      heading: 'How the Week Unfolded',
      paragraphs: [
        'Sydney became a rhythm of adventure, food, walking, discovery, great conversations, and one more stop before calling it a night.',
      ],
      subsections: [
        {
          heading: 'Day 1',
          id: 'check-in',
          paragraphs: [
            'Arrival in Sydney, check-in at W Sydney - Darling Harbour, the Coogee to Bondi Coastal Walk, Harry\'s Soft Serve, Sculpture by the Sea, and our first team meal at Bondi Trattoria.',
          ],
        },
        {
          heading: 'Day 2',
          paragraphs: [
            'SXSW Sydney began, followed by dinner at Quay, an evening walk through The Rocks, historic pub stops, Coat of Arms Pizza at the Australian Heritage Hotel, and the walk back to W Sydney.',
          ],
        },
        {
          heading: 'Day 3',
          paragraphs: [
            'Conference time, Nine Dragons for lunch, Marrickville Hotel, the walk to 20 Chapel, and the unanimous favorite dinner of the trip.',
          ],
        },
        {
          heading: 'Day 4',
          paragraphs: [
            'Conference sessions, the ferry to Manly, 4 Pines Brew Pub, and time exploring Manly before returning across the harbour.',
          ],
        },
        {
          heading: 'Day 5',
          paragraphs: [
            'The Blue Mountains, Mountain Culture Beer Co., pre-dinner cocktails at Bar Torino, dinner at Margaret, and one last stop at Stitch Bar.',
          ],
        },
      ],
    },
    {
      heading: 'Conference',
      id: 'conference',
      paragraphs: [
        'SXSW Sydney brought together innovators, entrepreneurs, creators, and technologists from around the world. We spent our days attending sessions, exploring the expo, making new connections, and discovering fresh ideas.',
        'Rather than recap the conference, we\'d rather share everything that happened before, after, and in between.',
      ],
      subsections: [
        {
          heading: 'Conference Highlights',
          paragraphs: [],
          bullets: [
            '✅ Opening Sessions',
            '✅ Expo Hall',
            '✅ Networking Events',
            '✅ Team coffee breaks',
            '✅ Teddy Swims keynote session',
            '✅ Live concerts every afternoon at Tumbalong Park',
            '✅ Closing celebration',
          ],
        },
      ],
    },
    {
      heading: 'Adventures',
      paragraphs: [],
      subsections: [
        {
          heading: 'Ferry to Manly Beach',
          id: 'ferry-to-manly',
          paragraphs: [
            'A ride across Sydney Harbour is more than transportation. It\'s one of the city\'s iconic experiences. With incredible skyline views, fresh ocean air, and fish and chips by the beach waiting on the other side, it was the perfect way to spend an afternoon away from the conference.',
          ],
          images: [
            {
              src: mediaRef('adventures/sydney-manly-beach-crew-selfie.jpg'),
              alt: 'Reckon crew taking a selfie at Manly Beach in Sydney',
              caption: 'The crew at Manly Beach after the ferry ride across Sydney Harbour.',
              compact: true,
            },
          ],
          note: 'Photo gallery',
        },
        {
          heading: 'Coogee to Bondi Coastal Walk',
          id: 'bondi-to-coogee',
          paragraphs: [
            'One of the world\'s most beautiful coastal walks delivered everything we\'d hoped for: dramatic cliffs, golden beaches, turquoise water, and countless opportunities to stop and take it all in.',
            'We started in Coogee and followed the coastline north toward Bondi, which gave the walk a perfect sense of build: quiet cliffs and ocean views at the start, sculpture installations along the way, and the classic Bondi Beach finish waiting at the end.',
            'As if the scenery wasn\'t enough, our walk happened to coincide with Sculpture by the Sea, the world\'s largest free outdoor sculpture exhibition. Dozens of large-scale art installations were displayed along the coastal trail, turning an already spectacular walk into an open-air gallery where every bend in the path revealed another piece of art set against the backdrop of the Pacific Ocean.',
            'A quick stop at Harry\'s Soft Serve in Coogee made for the perfect pre-walk treat before heading out across the cliffs.',
            'It was the perfect way to shake off the long flight, stretch our legs, and begin experiencing Sydney before the conference even started.',
          ],
          images: [
            {
              src: mediaRef('adventures/sydney-bondi-coogee-sculpture-tree.jpg'),
              alt: 'Sculpture by the Sea tree installation overlooking the Bondi coastline',
              caption: 'Sculpture by the Sea turned the coastal walk into an outdoor gallery.',
              compact: true,
            },
            {
              src: mediaRef('adventures/sydney-bondi-coogee-sculpture-chair.jpg'),
              alt: 'Sculpture by the Sea chair installation on the rocks above the ocean',
              caption: 'A coastal sculpture installation overlooking the Pacific.',
              compact: true,
            },
            {
              src: mediaRef('adventures/sydney-bondi-coogee-sculpture-seal.jpg'),
              alt: 'Sculpture by the Sea seal installation along the Coogee to Bondi walk',
              caption: 'Art installations appeared around each bend of the trail.',
              compact: true,
            },
            {
              src: mediaRef('adventures/sydney-bondi-coogee-bondi-beach-flowers.jpg'),
              alt: 'Bondi Beach coastline with orange flowers in the foreground',
              caption: 'Bondi Beach came into view as we made the final stretch of the walk.',
              compact: true,
            },
            {
              src: mediaRef('adventures/sydney-bondi-coogee-coastline.jpg'),
              alt: 'Rocky coastline and surf along the Coogee to Bondi coastal walk',
              caption: 'Rocky cliffs, ocean views, and sculpture installations along the trail.',
              compact: true,
            },
            {
              src: mediaRef('adventures/sydney-bondi-coogee-sculpture-painter.jpg'),
              alt: 'Playful painter sculpture along the Coogee to Bondi walk',
              caption: 'A playful Sculpture by the Sea installation along the walk.',
              compact: true,
            },
            {
              src: mediaRef('adventures/sydney-bondi-coogee-bondi-beach.jpg'),
              alt: 'Bondi Beach viewed from above with purple flowers in the foreground',
              caption: 'Bondi Beach from above as we finished the Coogee-to-Bondi walk.',
              compact: true,
            },
          ],
          note: 'Photo gallery',
        },
        {
          heading: 'Scenic World & the Blue Mountains',
          id: 'blue-mountains',
          paragraphs: [
            'A day in the Blue Mountains gave us a chance to experience another side of Australia. We rode the Scenic Railway through the rainforest, crossed the valley on the Scenic Skyway, explored the boardwalks below, and took in unforgettable views of the Three Sisters and Jamison Valley.',
          ],
          note: 'Photo gallery',
        },
        {
          heading: 'Exploring Sydney',
          paragraphs: [
            'Some of our favorite memories came from simply wandering the city between conference sessions and dinners.',
            'Sometimes the best discoveries weren\'t planned. They happened while walking to dinner, catching a ferry, or turning down a street that looked interesting.',
          ],
          bullets: [
            '⭐ Sydney Opera House',
            '⭐ Sydney Harbour Bridge',
            '⭐ Circular Quay',
            '⭐ Evening walk through The Rocks',
            '⭐ Darling Harbour',
            '⭐ Coffee shops around every corner',
          ],
        },
      ],
    },
    {
      heading: 'Dining Along the Way',
      paragraphs: [
        'One of the best ways to experience a city is around the table. Every meal gave us the chance to unwind after a full day, discover another neighborhood, and experience Sydney\'s incredible food and drink scene together.',
      ],
    },
    {
      heading: 'Bondi Trattoria',
      id: 'bondi-trattoria',
      subheading: 'Beachfront Italian at Bondi Beach',
      paragraphs: [],
      calloutLabel: 'Why We Remember It:',
      callout: 'Our first team meal in Sydney came after finishing the Coogee to Bondi Coastal Walk and spending time around Bondi Beach. Sitting just steps from the sand, it was the perfect place to settle in, soak up the atmosphere, and officially kick off an unforgettable week together.',
      images: [
        {
          src: mediaRef('adventures/sydney-bondi-trattoria.jpg'),
          alt: 'Reckon crew gathered around a table at Bondi Trattoria in Sydney',
          caption: 'The first team meal in Sydney at Bondi Trattoria.',
          compact: true,
        },
      ],
      note: 'Photo gallery',
    },
    {
      heading: 'Quay',
      id: 'quay',
      subheading: 'Sydney Fine Dining',
      paragraphs: [],
      calloutLabel: 'Why We Remember It:',
      callouts: [
        'An unforgettable evening with exceptional food, impeccable service, and one of the most spectacular dining views in the world overlooking Sydney Harbour, the Opera House, and the Harbour Bridge.',
        'After dinner at Quay, we wandered through The Rocks, stopping at several historic pubs before making the walk back toward Darling Harbour. It became one of those evenings where there was no real agenda - just exploring one of Sydney\'s oldest neighborhoods at our own pace.',
      ],
      subsections: [
        {
          heading: 'Glenmore Hotel',
          id: 'glenmore-hotel',
          paragraphs: [
            'Historic rooftop pub overlooking Sydney Harbour.',
          ],
        },
        {
          heading: 'Fortune of War Hotel',
          id: 'fortune-of-war-hotel',
          paragraphs: [
            'Australia\'s oldest continually licensed pub.',
          ],
        },
        {
          heading: 'Walk Back to W Sydney',
          id: 'walk-back-to-w-sydney',
          paragraphs: [
            'The night ended the same way so many great city nights do: walking back through the streets together, still talking about dinner, the pubs, the views, and everything we had managed to discover without really planning it.',
          ],
        },
      ],
      images: [
        {
          src: mediaRef('adventures/sydney-quay-opera-house-view.jpg'),
          alt: 'Wine bottles in front of the Sydney Opera House view from Quay',
          caption: 'The Opera House view from dinner at Quay.',
          compact: true,
        },
        {
          src: mediaRef('adventures/sydney-quay-team-dinner.jpg'),
          alt: 'Reckon crew seated together at Quay with the Sydney Opera House behind them',
          caption: 'The crew gathered at Quay with Sydney Harbour behind them.',
          compact: true,
        },
      ],
      note: 'Photo gallery',
    },
    {
      heading: 'Coat of Arms Pizza',
      id: 'coat-of-arms-pizza',
      subheading: 'Australian Heritage Hotel',
      paragraphs: [],
      calloutLabel: 'Why We Remember It:',
      callout: 'When in Australia... try kangaroo and emu. The Coat of Arms Pizza at the Australian Heritage Hotel was one of those experiences that had everyone talking. Reviews were mixed, but nobody regretted giving it a try.',
      images: [
        {
          src: mediaRef('adventures/sydney-australian-heritage-hotel.jpg'),
          alt: 'Reckon crew sharing Coat of Arms Pizza at the Australian Heritage Hotel',
          caption: 'Coat of Arms Pizza at the Australian Heritage Hotel.',
          compact: true,
        },
      ],
      note: 'Photo gallery',
    },
    {
      heading: 'Nine Dragons',
      id: 'nine-dragons',
      subheading: 'Traditional Cantonese Dim Sum for Lunch',
      paragraphs: [],
      calloutLabel: 'Why We Remember It:',
      callout: 'For most of the team, this was their first experience with dim sum. I ordered many of the classics I grew up enjoying during Sunday trips to Philadelphia\'s Chinatown with my family, expecting a nostalgic meal. Instead, I was blown away. Nine Dragons\' take on those familiar dishes was even better than I remembered. Watching everyone discover dim sum for the first time made the experience even more memorable.',
      note: 'Photo gallery',
    },
    {
      heading: 'Marrickville Hotel',
      id: 'marrickville-hotel',
      subheading: 'Pre-Dinner Drinks in Marrickville',
      paragraphs: [],
      calloutLabel: 'Why We Remember It:',
      callout: 'Before dinner at 20 Chapel, we stopped at the Marrickville Hotel for a drink and a chance to experience another Sydney neighborhood. It was the kind of casual, local stop that made the evening feel less like a reservation and more like an adventure.',
      note: 'Photo gallery',
    },
    {
      heading: 'Walk to 20 Chapel',
      id: 'walk-to-20-chapel',
      paragraphs: [
        'From Marrickville, we set off on foot toward 20 Chapel. As the streets grew quieter and the route started looking more like a business park than a dinner destination, more than one of us wondered whether the GPS had taken creative liberties.',
      ],
    },
    {
      heading: '20 Chapel',
      id: '20-chapel',
      subheading: 'The Group Favorite',
      paragraphs: [],
      calloutLabel: 'Why We Remember It:',
      callouts: [
        'We stopped in Marrickville for a drink before dinner, then set off on foot to 20 Chapel. As we wandered through quiet neighborhoods and what looked more like a business park than a dining destination, more than one of us started questioning whether the GPS had led us astray.',
        'It hadn\'t.',
        'Hidden at the end of the walk was the unanimous favorite restaurant of the trip. Sometimes the best meals are the ones you have to trust the journey to find.',
      ],
      images: [
        {
          src: mediaRef('adventures/sydney-20-chapel-team-dinner.jpg'),
          alt: 'Reckon crew gathered around the table for dinner at 20 Chapel in Sydney',
          caption: 'The team dinner at 20 Chapel, the unanimous group favorite.',
          compact: true,
        },
      ],
      note: 'Photo gallery',
    },
    {
      heading: '4 Pines Brew Pub - Manly',
      id: '4-pines',
      subheading: 'A Ferry Ride Well Rewarded',
      paragraphs: [],
      calloutLabel: 'Why We Remember It:',
      callout: 'After crossing Sydney Harbour by ferry to Manly, we settled into the original 4 Pines Brew Pub for lunch and a local craft beer. It was the perfect way to enjoy Manly\'s laid-back atmosphere before wandering the beach and catching the ferry back to the city.',
      images: [
        {
          src: mediaRef('adventures/sydney-4-pines-team-lunch.jpg'),
          alt: 'Reckon crew gathered around a table at 4 Pines Brew Pub in Manly',
          caption: 'A well-earned stop at 4 Pines after the ferry ride to Manly.',
          compact: true,
        },
      ],
      note: 'Photo gallery',
    },
    {
      heading: 'Mountain Culture Beer Co.',
      id: 'mountain-culture',
      subheading: 'Award-Winning Australian Craft Brewery',
      paragraphs: [],
      calloutLabel: 'Why We Remember It:',
      callouts: [
        'On the drive back to Sydney after spending the day exploring the Blue Mountains, we made one last stop at Mountain Culture Beer Co. What started as a quick break turned into one of the highlights of the day. Great beer, a welcoming atmosphere, and the kind of place that makes you wish you had just a little more time before heading back to the city.',
        'If we ever find ourselves back in the Blue Mountains, this will be one of our first stops.',
      ],
      images: [
        {
          src: mediaRef('adventures/sydney-mountain-culture-beer.jpg'),
          alt: 'A beer on the table at Mountain Culture Beer Co. in the Blue Mountains',
          caption: 'A pint of Status Quo, their award-winning hazy New England-style pale ale. Unfortunately its the only photo from an unforgettable brewery.',
          compact: true,
        },
      ],
      note: 'Photo gallery',
    },
    {
      heading: 'Bar Torino',
      id: 'bar-torino',
      subheading: 'Pre-Dinner Cocktails',
      paragraphs: [
        'After returning from the Blue Mountains, Bar Torino became the reset point before dinner. A round of cocktails, a little time to catch our breath, and then we were off to Margaret.',
      ],
    },
    {
      heading: 'Margaret',
      id: 'margaret',
      subheading: "One of the World's Best Steakhouses",
      paragraphs: [],
      bullets: [
        'Named the No. 2 Best Steak Restaurant in the World (2026)',
        'Ranked No. 1 Steak Restaurant in Australia (2026)',
      ],
      calloutLabel: 'Why We Remember It:',
      callout: 'Dining at Margaret wasn\'t just another team dinner. It was the opportunity to experience one of the world\'s finest steakhouses. It more than lived up to its reputation with exceptional food, outstanding hospitality, and a memorable evening together.',
      images: [
        {
          src: mediaRef('adventures/sydney-margaret-team-dinner.jpg'),
          alt: 'Reckon crew seated outside at Margaret for dinner in Sydney',
          caption: 'Team dinner at Margaret, one of the world\'s best steakhouses.',
          compact: true,
        },
      ],
      note: 'Photo gallery',
    },
    {
      heading: 'Stitch Bar',
      id: 'stitch-bar',
      paragraphs: [
        'One last Sydney stop before heading home. By then, the week had already become the kind of trip that would be retold through meals, walks, pub stops, and the little moments in between.',
      ],
    },
    {
      heading: 'Pubs... Too Many to List',
      paragraphs: [
        'Sydney\'s pub scene became an adventure all its own.',
      ],
      bullets: [
        'Historic pubs',
        'Rooftop bars',
        'Harbor-side pints',
        'Hidden neighborhood favorites',
        'Discovering a new pub (or two or three) every night',
      ],
      calloutLabel: 'Final tally:',
      callout: 'We honestly lost count.',
      images: [
        {
          src: mediaRef('adventures/sydney-pubs-opera-house-rooftop.jpg'),
          alt: 'Reckon crew at a rooftop pub with the Sydney Opera House glowing in the background',
          caption: 'A rooftop pub stop with the Sydney Opera House glowing across the harbour.',
          compact: true,
        },
        {
          src: mediaRef('adventures/sydney-pubs-outdoor-table.jpg'),
          alt: 'Reckon crew gathered around an outdoor pub table in Sydney',
          caption: 'Another night, another pub table, and another round of Sydney stories.',
          compact: true,
        },
      ],
      note: 'Photo gallery',
    },
    {
      heading: 'Things We Learned',
      paragraphs: [
        'Every destination teaches you something. Sydney gave us a few lessons we\'ll never forget.',
      ],
      bullets: [
        '☕ Australians take their coffee very seriously.',
        '🥛 Flat whites absolutely live up to the hype.',
        '🍺 Australia has an outstanding craft beer scene.',
        '🍕 Kangaroo and emu actually make a surprisingly good pizza.',
        '🍺 Despite looking everywhere... we never found a Foster\'s.',
        '😄 Rumor has it we enjoyed SXSW Sydney so much they decided to end on a high note.',
      ],
    },
    {
      heading: 'Favorite Moments',
      paragraphs: [
        'Some memories stand out long after the trip ends.',
      ],
      bullets: [
        '⭐ Watching Sydney Harbour come alive at sunset after dinner.',
        '⭐ Ending every conference day with live music in Tumbalong Park.',
        '⭐ Stumbling upon Sculpture by the Sea during the Coogee to Bondi Coastal Walk.',
        '⭐ Starting the Coogee to Bondi walk with Harry\'s Soft Serve.',
        '⭐ Walking through The Rocks after dinner instead of heading straight back to the hotel.',
        '⭐ Discovering Australia\'s oldest continually licensed pub.',
        '⭐ Discovering a new pub (or two or three) every night.',
        '⭐ Riding the ferries around Sydney Harbour.',
        '⭐ Walking the Coogee to Bondi Coastal Walk.',
        '⭐ Watching everyone\'s reaction to their first dim sum experience.',
        '⭐ Finally finding 20 Chapel after questioning whether the GPS had completely lost its mind.',
      ],
    },
    {
      heading: 'The Reckon Crew',
      paragraphs: [],
      links: [
        { label: 'Kevin L.', route: { name: 'passport', params: { employeeSlug: 'kevin-l' } } },
        { label: 'Brandon A.', route: { name: 'passport', params: { employeeSlug: 'brandon-a' } } },
        { label: 'Brandon L.', route: { name: 'passport', params: { employeeSlug: 'brandon-l' } } },
        { label: 'Matt G.', route: { name: 'passport', params: { employeeSlug: 'matt-g' } } },
        { label: 'Ben B.', route: { name: 'passport', params: { employeeSlug: 'ben-b' } } },
        { label: 'Phillip H.', route: { name: 'passport', params: { employeeSlug: 'phillip-h' } } },
      ],
    },
    {
      heading: 'By the Numbers',
      paragraphs: [],
      rows: [
        { label: '✈️ Flights', value: '4' },
        { label: '🌍 Time Zones Crossed', value: '15' },
        { label: '🎤 Conference', value: '1' },
        { label: '🍽️ Team Dinners', value: '4' },
        { label: '🥟 First-Time Dim Sum Experiences', value: '5' },
        { label: '🍺 Brewery Visits', value: '1' },
        { label: '🍻 Pubs', value: 'Lost count' },
        { label: '🎵 Live Concerts', value: 'Every afternoon' },
        { label: '🚢 Ferry Rides', value: 'Many' },
        { label: '🥾 Iconic Coastal Walks', value: '1' },
        { label: '🚠 Scenic Railways', value: '1' },
        { label: '📸 Photos Taken', value: 'Thousands' },
        { label: '😂 Laughs Shared', value: 'Countless' },
      ],
    },
    {
      heading: 'Photo Gallery',
      paragraphs: [],
      subsections: [
        {
          heading: '📸 Conference',
          paragraphs: [],
          bullets: [
            'Opening sessions',
            'Expo Hall',
            'Teddy Swims keynote',
            'Team coffee breaks',
            'Networking',
            'Live concerts at Tumbalong Park',
          ],
        },
        {
          heading: '📸 Adventures',
          paragraphs: [],
          bullets: [
            'Ferry to Manly Beach',
            'Coogee to Bondi Coastal Walk',
            'Scenic World',
            'Blue Mountains',
            'Sydney Harbour',
            'Evening walk through The Rocks',
          ],
        },
        {
          heading: 'Dining Along the Way',
          paragraphs: [],
          bullets: [
            'Quay',
            'Margaret',
            '20 Chapel',
            'Nine Dragons',
            '4 Pines Brew Pub - Manly',
            'Mountain Culture Beer Co.',
            'Bar Torino',
            'Stitch Bar',
            'Glenmore Hotel',
            'Fortune of War Hotel',
            'Coat of Arms Pizza',
            'A few of the many pubs',
          ],
        },
        {
          heading: '📸 The Crew',
          paragraphs: [
            'The best photos are usually the unplanned ones.',
          ],
          bullets: [
            'Walking between sessions',
            'Around the dinner table',
            'Exploring Sydney',
            'Harbor selfies',
            'Laughing over a shared meal',
            'Sunset conversations',
            'The moments between the itinerary',
          ],
        },
      ],
    },
    {
      heading: 'Merit Badges Earned',
      paragraphs: [],
      bullets: [
        '🏅 Ferry to Manly Beach',
        '🏅 Coogee to Bondi Coastal Walk',
        '🏅 Scenic World – Blue Mountains',
      ],
    },
    {
      heading: 'Looking Back',
      paragraphs: [
        'SXSW Sydney gave us a reason to visit one of the world\'s great cities.',
        'The conference filled our days, but Sydney filled everything else.',
        'From award-winning restaurants and hidden neighborhood gems to ferry rides across the harbor, afternoons filled with live music, and discovering a new pub (or two or three) every night, we experienced Sydney the way we hope to experience every destination: as a team.',
      ],
      emphasis: 'That\'s the Reckon Way.',
    },
  ],
};

export const corporateRetreatGalleries = [
  {
    slug: 'iceland-corporate-retreat-2026',
    legacySlugs: ['iceland-2026'],
    title: 'Iceland Corporate Retreat',
    subtitle: 'Fire, Ice, and the Reckon Way',
    meta: 'Corporate Retreat',
    description: 'A family-inclusive retreat through glaciers, geothermal lagoons, unforgettable meals, and shared firsts in the land of fire and ice.',
    destination: {
      country: 'Iceland',
      continent: 'Europe',
    },
    dateLabel: 'March 2026',
    tripType: 'Corporate Retreat',
    summary: 'A family-inclusive retreat through glaciers, geothermal lagoons, unforgettable meals, and shared firsts in the land of fire and ice.',
    attendees: [],
    tags: tagRefs(
      'corporate-retreat',
      'family-friendly',
      'international',
      'iceland',
      'adventure',
      'snowmobiling',
      'road-trip',
      'glacier',
      'hot-springs',
      'luxury',
      'fine-dining',
      'nature',
      'bucket-list',
      'reckon-retreats',
      'iceland-collection',
      'best-restaurants',
      'family-adventures',
    ),
    achievements: achievementRefs(
      'snowmobiling-langjokull',
      'south-coast-to-reykjavik',
    ),
    firsts: firstRefs(
      'first-glacier',
      'first-snowmobile',
      'first-geothermal-lagoon',
    ),
    familyFriendly: true,
    familyNote: 'Loved ones were invited to join this adventure.',
    experienceDNA: {
      food: 5,
      adventure: 5,
      family: 5,
      nightlife: 1,
      nature: 5,
      culture: 3,
      relaxation: 4,
      luxury: 4,
    },
    coverImage: mediaRef('adventures/iceland-2026.png'),
    heroImage: mediaRef('adventures/iceland-heroes-no-text.png'),
    tripCard: {
      tripSlug: 'iceland-corporate-retreat-2026',
      title: 'Iceland Corporate Retreat',
      subtitle: 'Fire, Ice, and the Reckon Way',
      destination: 'Iceland',
      date: 'March 2026',
      tripType: 'Corporate Retreat',
      heroImage: mediaRef('adventures/iceland-2026.png'),
      badgeIcon: '🇮🇸',
      crewCount: 0,
      familyIncluded: true,
      familyFriendly: true,
      familyNote: 'Loved ones were invited to join this adventure.',
      featuredActivities: [
        'Snowmobiling on Langjökull Glacier',
        'Blue Lagoon',
        'Laugarás Lagoon',
        'South Coast Adventure',
        'Reykjavík',
      ],
      featuredMeals: [
        'Lava',
        'Torfhús Retreat',
        'Ylja',
        'Fish Company',
        'Grillmarkaðurinn',
      ],
      earnedBadges: [
        'Snowmobiling Langjökull',
        'South Coast to Reykjavík',
        'Laugarás Lagoon',
      ],
      summary: 'A family-inclusive retreat through glaciers, lagoons, unforgettable meals, and shared firsts in the land of fire and ice.',
      ctaText: 'View Adventure',
      ctaUrl: '/the-reckon-way/iceland-corporate-retreat-2026',
    },
    prologueStory: {
      id: 'why-we-go',
      slug: 'why-we-go',
      title: 'Why We Go',
      summary: 'The destination matters. But it isn\'t the most important part. The people are.',
      dayLabel: 'Prologue',
      route: '/the-reckon-way/stories/why-we-go',
    },
    journeyDays: [
      {
        id: 'day-1-flight', label: 'Day 1 · Travel', value: 'Gate A24', status: 'published',
      },
      {
        id: 'day-2-arrival', label: 'Day 2 · Arrival', value: 'Blue Lagoon & Torfhús', status: 'coming-soon',
      },
      {
        id: 'day-3-glacier', label: 'Day 3 · Adventure', value: 'Langjökull & Laugarás', status: 'coming-soon',
      },
      {
        id: 'day-4-road-trip', label: 'Day 4 · Road Trip', value: 'South Coast to Reykjavík', status: 'coming-soon',
      },
      {
        id: 'day-5-reykjavik', label: 'Day 5 · Reykjavík', value: 'A Day in the City', status: 'coming-soon',
      },
      {
        id: 'day-6-home', label: 'Day 6 · Travel', value: 'The Journey Home', status: 'coming-soon',
      },
    ],
    blogPosts: [
      {
        eyebrow: 'Travel Blog',
        title: 'Why We Chose Iceland',
        released: false,
        lede: 'Building Culture at the Edge of the World.',
        sections: [
          {
            heading: 'A Different Kind of Retreat',
            paragraphs: [
              'When people hear the words company retreat, they often picture conference rooms, keynote speakers, and carefully planned agendas.',
              'There\'s certainly value in stepping away from the office to think strategically. But when we began planning our 2026 retreat, we wanted something different.',
              'We weren\'t looking for another offsite.',
              'We wanted to create an experience.',
              'One that would challenge us.',
              'One that would inspire us.',
              'One that would strengthen relationships long after everyone returned home.',
              'That search led us to Iceland.',
            ],
            images: [
              {
                src: mediaRef('adventures/iceland-blog/iceland-group-viking-hats.jpg'),
                alt: 'Reckon team and families standing between snowy lava rocks in Iceland',
                caption: 'Team and family moments on Iceland\'s winter coastline.',
                wide: true,
              },
            ],
          },
          {
            heading: 'Why Iceland?',
            paragraphs: [
              'There are easier destinations.',
              'There are warmer destinations.',
              'There are certainly destinations with more predictable weather.',
              'But very few places capture the imagination quite like Iceland.',
              'It\'s a country where glaciers rest beside volcanoes. Waterfalls carve through ancient lava fields. Steam rises from the earth while snow blankets mountains in the distance.',
              'It\'s a place that reminds you just how extraordinary our world really is.',
              'And perhaps more importantly, it reminds you that there is always something new to discover.',
              'That spirit of curiosity felt like the perfect match for our team.',
              'Iceland is also a place that rewards curiosity. Around every bend in the road is something unexpected. A waterfall appears where you least expect it. Steam rises from the ground in the middle of a snow-covered landscape. The weather changes by the minute, reminding you that nature is always in charge.',
              'That sense of discovery was exactly what we hoped to create for everyone joining us.',
            ],
            images: [
              {
                src: mediaRef('adventures/iceland-blog/iceland-frozen-waterfall.jpg'),
                alt: 'Frozen Iceland waterfall surrounded by snow and ice',
                caption: 'Frozen waterfalls framed by snow and mist.',
              },
              {
                src: mediaRef('adventures/iceland-blog/iceland-snowy-lake-rock.jpg'),
                alt: 'Snowy Iceland shoreline with black lava rock beside a lake',
                caption: 'Black lava rock, winter shoreline, and the scale of Iceland\'s landscape.',
              },
            ],
          },
          {
            heading: 'We Didn\'t Want to Plan a Vacation',
            paragraphs: [
              'We wanted to design an experience.',
              'There\'s a difference.',
              'Vacations are often about escape.',
              'Experiences are about growth.',
              'Every stop on our itinerary was chosen intentionally.',
              'Some would challenge us.',
              'Some would encourage us to slow down.',
              'Others would simply create opportunities to connect.',
              'We would soak in geothermal lagoons, stand on ancient glaciers, explore landscapes unlike anything we had seen before, share incredible meals, and spend time together away from the routines of everyday life.',
              'None of those experiences were the destination.',
              'They were simply the setting.',
              'Because the real purpose of the retreat wasn\'t Iceland.',
              'It was the people sharing it together.',
            ],
          },
          {
            heading: 'Why Families Matter',
            paragraphs: [
              'One thing that makes our retreats unique is that they\'re not just for employees.',
              'They\'re for the people who make our employees who they are.',
              'The spouses who celebrate the victories.',
              'The partners who support the late nights.',
              'The children who hear stories about coworkers they\'ve never met.',
              'Inviting families changes everything.',
              'Work conversations become life conversations.',
              'Coworkers become friends.',
              'Children create friendships of their own.',
              'Everyone gets to know one another beyond job titles and Zoom meetings.',
              'Culture isn\'t built during meetings.',
              'It\'s built through shared experiences.',
              'When families are included, those experiences become even more meaningful.',
            ],
            images: [
              {
                src: mediaRef('adventures/iceland-blog/iceland-kids-in-snow.jpg'),
                alt: 'Children sitting in deep snow during the Iceland retreat',
                caption: 'The youngest adventurers finding their own Iceland memories.',
              },
            ],
          },
          {
            heading: 'Adventure Is the Great Equalizer',
            paragraphs: [
              'One of the reasons we love experiential travel is that everyone starts in the same place.',
              'Curious.',
              'Excited.',
              'Maybe just a little nervous.',
              'Whether you\'re an engineer, recruiter, executive, spouse, or child, nobody arrives already knowing how to navigate Iceland.',
              'Shared firsts have a remarkable way of bringing people together.',
              'They replace introductions with stories.',
              'They replace small talk with laughter.',
              'They create moments of encouragement when someone tries something for the first time.',
              'And they become memories that people carry with them long after the trip is over.',
              'Adventure has a way of reminding us that we\'re all learning together.',
            ],
          },
          {
            heading: 'Home Away From Home',
            paragraphs: [
              'Every adventure needs a place to gather.',
              'For us, that place was Torfhus Retreat.',
              'Inspired by Iceland\'s traditional turf houses, it felt less like checking into a hotel and more like stepping into another era. Snow-covered rooftops blended into the surrounding landscape, private geothermal hot tubs sat outside each cabin, and the pace of life slowed the moment we arrived.',
              'It became more than a place to sleep.',
              'It became our home.',
              'Some of the most meaningful conversations of the week didn\'t happen during an activity.',
              'They happened around fireplaces.',
              'Over morning coffee.',
              'In steaming hot tubs beneath the Icelandic sky.',
              'Or while simply watching the snow fall outside.',
              'Sometimes the places where you pause become just as memorable as the places where you explore.',
            ],
            images: [
              {
                src: mediaRef('adventures/iceland-blog/iceland-snow-field-clouds.jpg'),
                alt: 'Wide snow-covered Iceland landscape under dramatic clouds',
                caption: 'Wide-open winter light across Iceland\'s snow-covered countryside.',
                wide: true,
              },
            ],
          },
          {
            heading: 'The Best Moments Can\'t Be Scheduled',
            paragraphs: [
              'As carefully as we planned this retreat, we knew something from the beginning.',
              'The moments people remember most rarely appear on an itinerary.',
              'They are the unexpected conversations during a scenic drive.',
              'The laughter around a dinner table.',
              'The child seeing snow piled higher than they are.',
              'The quiet moment when someone stops talking because the landscape leaves them speechless.',
              'The friendships that begin without anyone realizing it.',
              'Those moments can\'t be planned.',
              'They simply happen when you create the opportunity.',
              'That\'s exactly what we hoped Iceland would provide.',
              'It exceeded every expectation.',
            ],
          },
          {
            heading: 'What We Hoped to Bring Home',
            paragraphs: [
              'Of course, we hoped everyone would come home with incredible photographs.',
              'Maybe a few souvenirs.',
              'Certainly a few stories.',
              'But those weren\'t the real goals.',
              'We hoped people would return with stronger friendships.',
              'A deeper appreciation for one another.',
              'Fresh perspectives.',
              'Renewed energy.',
              'And a collection of shared experiences that would continue to shape how we work together long after the suitcases were unpacked.',
              'Because projects eventually end.',
              'Relationships endure.',
              'That\'s what this retreat was really about.',
            ],
          },
          {
            heading: 'This Is Just the Beginning',
            paragraphs: [
              'Over the coming weeks, we will share the stories behind this remarkable journey.',
              'You will join us as an unexpected delay tests our patience before we even leave the ground, experience Iceland\'s geothermal wonders, race across Langjokull Glacier in a surprise blizzard, gather around unforgettable meals, explore landscapes unlike anywhere else on Earth, and discover that the greatest memories often come from the moments no one could have planned.',
              'Because this series isn\'t really about Iceland.',
              'It\'s about what happens when you invest in people.',
              'When you step outside your comfort zone.',
              'When you bring families together.',
              'When you make time to slow down.',
              'And when you discover that the strongest teams aren\'t just built through the work they do, but through the experiences they share.',
              'Welcome to the journey.',
            ],
          },
        ],
      },
      {
        eyebrow: 'Travel Blog',
        title: 'Gate A24',
        slug: 'gate-a24',
        legacySlugs: ['before-we-even-left-the-ground'],
        released: true,
        dayLabel: 'Day 1 · Flight',
        lede: 'Sometimes the Journey Begins Before the Plane Takes Off.',
        sections: [
          {
            heading: 'Sometimes the Journey Begins Before the Plane Takes Off',
            paragraphs: [
              "Every great adventure has a moment when you wonder if it's actually going to happen.",
              'For us, that moment came before we ever left Washington Dulles International Airport.',
              'Months of planning had led to this day.',
              'Flights had been booked.',
              'Passports had been checked and rechecked.',
              'Packing lists had been revised more times than anyone would care to admit.',
              'After months of anticipation, the day had finally arrived.',
              'And, almost surprisingly, everything was going exactly according to plan.',
              'Our family, Kathy, Chase, Holden, and I, made it through security with plenty of time before boarding. Rather than heading straight to the gate, we stopped at Fiolina for dinner.',
              "Fresh handmade pasta wasn't exactly what I expected to eat before boarding an overnight flight to Iceland, but it turned out to be the perfect way to begin the trip.",
              'For a little while, it was just the four of us.',
              'No boarding announcements.',
              'No delays.',
              'No schedules.',
              'Just one last family dinner before the adventure officially began.',
              "Over the next week we'd share meals throughout Iceland, inside a cave overlooking a volcanic lake, at Torfhús, around some of Reykjavík's best restaurants and even the infamous Bæjarins Beztu Pylsur hot dog stand!",
              'We were beginning one of the themes that would reoccur during the entire trip, memories being made around various meal settings.',
              'As we made our way toward Gate A24, we found familiar faces already waiting from our small but mighty Reckon Crew.',
              'Matt and his wife, Paulina, were there with their two young kids, who sat quietly in their stroller prepared for the trip like the most veteran travelers. Brandon A. and Shannon, were already settled in nearby, quietly talking about everything they hoped to see during the week. Philip had found a seat nearby and was quietly watching the activity around the gate while chatting with the group.',
              "Conversations bounced between what everyone had packed, which activities they were most looking forward to, and predictions about Iceland's famously unpredictable weather. There was an unmistakable excitement in the air that only comes before a big adventure.",
              'The other half of our group had flown to Iceland a day early to explore Reykjavík before the retreat officially began. As we waited to board, our Slack channel came alive with photos from the North Atlantic.',
              "Al, Ben, Brandon L., Jasmine, and Marcus were already wandering the colorful streets of Reykjavík. A little later, another photo appeared of them gathered around a table at one of the city's pubs, smiling with local drinks in hand. Every new picture made the rest of us just a little more eager to get there.",
              'It felt like we were already sharing the experience, even from thousands of miles away.',
              'Then a notification appeared on our phones.',
              'Icelandair Flight 644',
              'Flight Delayed – 20 Minutes',
              'Kathy looked up from her phone and said, "I\'m going to see if they know what\'s going on."',
              'Before I could respond, she was already making her way toward the gate podium.',
              'Kathy returned a few minutes later, smiling.',
              '"Well?"',
              '"The good news is I found the gate agent."',
              '"The bad news?"',
              '"They know exactly as much as we do."',
              'We laughed.',
              'There wasn\'t much else to do besides settle back into our seats and keep an eye on the departure board.',
              'At first, nobody thought much of it.',
              'Travel delays happen.',
              "Twenty minutes wasn't enough to dampen anyone's spirits. We laughed, refreshed the app, and figured we'd still be in Iceland before breakfast.",
              'Then our phones buzzed again.',
              "This time the delay wasn't twenty minutes.",
              'It was an hour.',
              'The mood shifted.',
              'Nobody panicked, but everyone started doing what travelers always do when the information feels incomplete.',
              'Refreshing the app.',
              'Looking out the window.',
              'Checking the departure board.',
              'And then Phillip pulled up a flight tracking app.',
              "\"I think that's our airplane...\"",
              'A small group gathered around the screen.',
              'Instead of continuing toward Washington, our inbound aircraft had changed course.',
              'It was turning north.',
              'A few seconds later, there was no denying it.',
              "The plane that was supposed to take us to Iceland wasn't coming to Dulles.",
              'It was diverted to Newark.',
              "Suddenly, this wasn't just a routine delay.",
              'Questions started flying.',
              'Why Newark?',
              'Was there a mechanical issue?',
              'Would they send another aircraft?',
              'Were we still leaving tonight?',
              'As more people searched for answers, bits of information started appearing online. Reports were circulating that the FAA had issued a ground stop for Washington Dulles, Reagan National, and Baltimore-Washington International. Flights across the region had been halted, and early reports suggested there had been an incident involving Potomac TRACON, the air traffic control facility responsible for managing much of the airspace around Washington, D.C.',
              "Whether every detail was accurate or not didn't really matter at that moment.",
              'What mattered was that nobody knew how long the ground stop would last.',
              "Or whether we'd make it to Iceland that night.",
              'Or ever.',
              'It suddenly felt like this could be a very long evening.',
              'There was one more thing on my mind.',
              'Somewhere in Iceland, our guide Hanna was preparing for our arrival.',
              'The itinerary had been carefully planned.',
              'Vehicles were waiting.',
              'Blue Lagoon reservations had been arranged.',
              'And we were still sitting at Gate A24, watching the departure board refresh over and over again, wondering if we\'d ever leave Washington.',
              'Fortunately, Hanna and I had already been communicating over WhatsApp in the days leading up to the trip, coordinating arrival times and the final details.',
              'I opened the chat and sent a quick message.',
              '"We\'re delayed... and it may be a while."',
              'Her response came back almost immediately.',
              '"No worries at all. We\'ll be here whenever you arrive. Just keep me updated. Travel safely."',
              "It wasn't a long conversation.",
              'But it was exactly what I needed to hear.',
              'She already had a plan.',
              'Knowing that made the uncertainty feel just a little smaller.',
            ],
            images: [
              {
                src: mediaRef('adventures/iceland-blog/iceland-early-arrival-airport.jpg'),
                alt: 'Members of the Reckon Crew arriving early at Keflavík Airport',
                caption: 'The early-arrival crew made it to Iceland a day ahead of the main group.',
                wide: true,
              },
              {
                src: mediaRef('adventures/iceland-blog/iceland-early-arrival-lunch.jpg'),
                position: 'center top',
                alt: 'Two Reckon Crew members sharing lunch and drinks in Reykjavík',
                caption: 'A first meal in Reykjavík while the rest of the crew waited at Gate A24.',
              },
              {
                src: mediaRef('adventures/iceland-blog/iceland-early-arrival-group-lunch.jpg'),
                alt: 'The early-arrival Reckon group gathered around a lunch table in Reykjavík',
                caption: 'The first Iceland stories were already taking shape around the table.',
                wide: true,
              },
              {
                src: mediaRef('adventures/iceland-blog/iceland-early-arrival-bastard-bar.jpg'),
                alt: 'The early-arrival group outside Bastard Brew and Food in Reykjavík',
                caption: 'Exploring Reykjavík together before the retreat officially began.',
              },
              {
                src: mediaRef('adventures/iceland-blog/iceland-early-arrival-pub-toast.jpg'),
                alt: 'Three members of the Reckon Crew raising drinks in a Reykjavík pub',
                caption: 'A Reykjavík toast shared back to the crew still waiting in Washington.',
              },
            ],
          },
          {
            heading: 'The First Lesson Came Earlier Than Expected',
            paragraphs: [
              "There wasn't a workaround.",
              'No shortcut.',
              'No one to call.',
              'We had done everything we could.',
              'Now all we could do was wait.',
              'So we settled in.',
              'Someone grabbed another cup of coffee.',
              'Kids found new ways to entertain themselves.',
              'The Slack channel continued filling with photos from Reykjavík while we joked that our coworkers were already living the adventure we were still waiting to begin.',
              'Conversations drifted from travel plans to work stories, family updates, and guesses about what would happen next.',
              'Somewhere along the way, the frustration started to fade.',
              'The uncertainty was still there.',
              'But so was the excitement.',
              'Oddly enough, it no longer felt like wasted time.',
              'It felt like the beginning of the retreat.',
              'Not because we were in Iceland.',
              'But because we were together.',
              'Then, almost as quickly as the uncertainty had arrived, another update appeared.',
              'The FAA had lifted the ground stop.',
              'A collective sigh of relief swept through the gate.',
              "But we weren't celebrating just yet.",
              'Our airplane was still sitting in Newark.',
              'Within seconds, half the group had become amateur air traffic controllers.',
              'Someone refreshed the flight tracker.',
              'Someone else announced every update like a sports broadcaster.',
              '"It\'s pushing back!"',
              'A few seconds later, the tiny airplane icon began to move.',
              'There it was.',
              'Heading south.',
              'Our airplane was finally on its way to Washington.',
              'As soon as we saw it in the air, I sent Hanna another message.',
              '"Looks like we\'re finally moving."',
              'A few moments later she replied.',
              '"Perfect. I\'ll be watching your flight. We\'ll be ready when you get here."',
              'Somehow, knowing someone in Iceland was following our progress made the trip feel like it had already begun.',
              'It felt like the first real victory of the night.',
              'Of course, we all knew there was still a long way to go.',
              'The aircraft still had to land.',
              'Taxi.',
              'Pull into a gate.',
              'Unload passengers.',
              'Be serviced.',
              'Board us.',
              'And then make the overnight journey across the Atlantic to the Land of Fire and Ice',
              'There were still plenty of opportunities for something else to go wrong.',
              'But for the first time that evening, hope had replaced uncertainty.',
              "Our adventure wasn't underway yet.",
              'But it finally felt possible.',
              "Sometimes adventure doesn't begin with a passport stamp.",
              'Sometimes it begins with a tiny airplane icon moving across a phone screen while thirty people quietly cheer in an airport gate.',
              'We still had a long night ahead.',
              'But at least now...',
              '...we had an airplane.',
            ],
          },
          {
            heading: 'Next in the Series: Landing in the Land of Fire and Ice',
            paragraphs: [
              'Eventually, the boarding announcement came. The wheels finally left the runway behind, and somewhere over the Atlantic the uncertainty gave way to anticipation. Ahead of us waited lava fields, geothermal waters, Viking turf houses, and a week of adventures none of us would ever forget. Iceland was finally within reach.',
            ],
          },
        ],
      },
      {
        eyebrow: 'Travel Blog',
        title: 'Landing in the Land of Fire and Ice',
        released: false,
        lede: 'Every Great Adventure Has a Moment When It Finally Feels Real.',
        sections: [
          {
            heading: 'Every Great Adventure Has a Moment When It Finally Feels Real',
            paragraphs: [
              "The boarding announcement finally came well after we'd expected it.",
              'After an evening of delays, diverted airplanes, and more time staring at a flight tracker than any of us ever imagined, we were finally on our way.',
              'As the aircraft climbed into the night sky, a quiet sense of relief settled over the cabin.',
              'The hardest part of the journey was behind us.',
              'For a while, conversations continued as everyone replayed the events of the evening. Eventually, the cabin lights dimmed. Some people managed a few hours of sleep. Others were simply too excited to rest, checking the flight map as our little airplane icon slowly made its way across the Atlantic.',
              "Morning couldn't come soon enough.",
            ],
          },
          {
            heading: 'First Light',
            paragraphs: [
              'Somewhere over the North Atlantic, the first hints of daylight began to appear.',
              'Window shades slowly opened throughout the cabin.',
              'People leaned toward the windows, hoping to catch their first glimpse of Iceland.',
              'Then someone quietly said,',
              '"There it is."',
              'Heads turned.',
              'Necks craned.',
              "Below us was a landscape unlike anything we'd ever seen.",
              'There were no sprawling cities.',
              'No patchwork of farmland.',
              'Instead, black lava fields stretched toward the horizon, broken only by snow-covered mountains and the deep blue waters of the Atlantic.',
              'Even from 30,000 feet, Iceland looked different.',
              'Wild.',
              'Untamed.',
              'As if nature had written its own rules here.',
              "It was hard to believe that just a few hours earlier we had been wondering whether we'd make it at all.",
            ],
          },
          {
            heading: 'Our First Breath of Iceland',
            paragraphs: [
              'Most international flights end the same way.',
              'You pull up to a jet bridge.',
              'You shuffle through the terminal.',
              "You don't really experience the place until much later.",
              'Iceland greeted us differently.',
              'Our aircraft came to a stop on the tarmac.',
              'When the cabin door opened, we stepped outside and walked down the stairs onto the runway before boarding a shuttle bus to the terminal.',
              'Before we saw much of Iceland...',
              'We felt it.',
              'The air was crisp.',
              'Cool without being harsh.',
              'Exceptionally clean.',
              "After nearly seven hours inside an airplane, that first breath felt refreshing in a way that's difficult to describe.",
              "It wasn't just colder than home.",
              'It smelled different.',
              'Cleaner.',
              'Fresher.',
              'For a few moments, nobody seemed to be in a hurry.',
              'People paused.',
              'Looked around.',
              'Pulled their jackets a little tighter.',
              'Smiled.',
              'It was one of those rare travel moments when you instantly realize...',
              "You're somewhere completely different.",
            ],
            images: [
              {
                src: mediaRef('adventures/iceland-blog/iceland-plane-arrival.jpg'),
                alt: 'Icelandair plane parked on the tarmac after landing in Iceland',
                caption: 'Our first step into Iceland started on the tarmac.',
                wide: true,
              },
            ],
          },
          {
            heading: 'The Quiet Bus Ride',
            paragraphs: [
              'The shuttle bus carried us across the tarmac toward the terminal.',
              "Normally, that's the most forgettable part of arriving somewhere.",
              "This time it wasn't.",
              "Everyone's eyes were fixed on the windows.",
              "The volcanic landscape we'd only seen in photographs was suddenly real.",
              'Black lava fields stretched in every direction.',
              'Bright green moss clung to ancient volcanic rock.',
              'Patches of snow lingered in the distance beneath an enormous Icelandic sky.',
              'There were almost no trees.',
              'No billboards.',
              'No suburban neighborhoods surrounding the airport.',
              'Just open space.',
              'No one said much.',
              'We were simply taking it all in.',
              'The waiting was over.',
              'We had finally made it to Iceland.',
            ],
          },
          {
            heading: 'A Familiar Face',
            paragraphs: [
              'Outside the terminal, a familiar smile was waiting for us.',
              'After exchanging messages throughout the previous evening, it felt a little strange that this was the first time we were actually meeting Hanna in person.',
              "She greeted us with the same calm confidence she'd shown over WhatsApp.",
              '"Welcome to Iceland."',
              'Simple words.',
              'But after everything it had taken to get there, they carried a little extra meaning.',
              'Within minutes, luggage was loaded into waiting vehicles, everyone was accounted for, and the adventure that had almost slipped away was finally underway.',
              'As we pulled away from Keflavik Airport, Hanna began introducing us to Iceland.',
              'Not with a rehearsed speech.',
              'With stories.',
              'Stories about the lava fields surrounding us.',
              'About how young this landscape really is.',
              'About volcanoes that continue to shape the island.',
              'About geothermal energy that heats homes, powers communities, and bubbles beneath the roads we were driving on.',
              'She spoke with the kind of pride that only comes from someone sharing their home.',
              "Before long, we weren't simply driving through Iceland.",
              'We were beginning to understand it.',
            ],
          },
          {
            heading: 'Seeing Iceland Through Local Eyes',
            paragraphs: [
              'One of the greatest gifts of traveling with a local guide is learning to notice the things you would have driven right past on your own.',
              'To us, the lava fields looked ancient.',
              'To Hanna, they each told a story.',
              'Some were centuries old.',
              'Others had been created in our lifetime.',
              "The thick green moss carpeting the rocks wasn't just beautiful.",
              'It was incredibly fragile, taking decades to recover if damaged.',
              'The landscape that looked empty at first glance was, in reality, full of life and history.',
              'It changed the way we looked out the window.',
              'We stopped searching for landmarks.',
              'Instead, we started appreciating the landscape itself.',
            ],
          },
          {
            heading: 'The Journey Was Finally Beginning',
            paragraphs: [
              "Twenty-four hours earlier, we had been standing at Gate A24, watching the departure board refresh over and over again and wondering if we'd even leave Washington.",
              'Now we were driving through one of the most remarkable landscapes on Earth.',
              'The delays.',
              'The uncertainty.',
              'The diverted airplane.',
              'Watching a tiny airplane icon crawl across a flight tracker.',
              'Those moments already felt less like obstacles and more like the opening chapter of the adventure.',
              'Ahead of us stretched miles of lava fields.',
              'Somewhere beyond them waited the rest of our team.',
              'And beyond that...',
              'Our first stop.',
              'A place chosen not just because it was beautiful, but because it offered exactly what we all needed after an unforgettable journey.',
              'A chance to slow down.',
              'To breathe.',
              'And to truly arrive.',
            ],
          },
          {
            heading: 'Next in the Series: The Art of Arriving',
            paragraphs: [
              "Our first stop in Iceland wasn't a hotel. It wasn't a meeting room. It was the Blue Lagoon - a place where warm geothermal waters, drifting steam, and the simple act of slowing down helped transform a group of travelers into a group ready for the adventure ahead.",
            ],
          },
        ],
      },
      {
        eyebrow: 'Travel Blog',
        title: 'The Art of Arriving',
        released: false,
        lede: 'Why Our Iceland Adventure Began at the Blue Lagoon.',
        sections: [
          {
            heading: 'Why Our Iceland Adventure Began at the Blue Lagoon',
            paragraphs: [
              "By the time we pulled into the Blue Lagoon parking lot, it felt as though we'd already lived an entire day.",
              'An unexpected ground stop.',
              'A diverted airplane.',
              'An overnight flight across the Atlantic.',
              'Our first glimpse of Iceland.',
              "A drive through Iceland's volcanic landscape while Hanna introduced us to her country.",
              'And now, finally, we had arrived at the place where our retreat would truly begin.',
              'Waiting for us in the parking lot was the rest of the team.',
              "The group that had arrived a day earlier was already there, smiling, rested, and eager to hear the story of the journey we'd taken just to get to Iceland.",
              'Their stories sounded very different from ours.',
              'They talked about wandering the streets of Reykjavik, discovering cozy restaurants, and spending the evening exploring the city.',
              'We laughed as we shared our own adventure - the delays, the diverted airplane, the endless refreshing of the United app, and the tiny airplane icon we had all watched crawl from Newark to Washington.',
              'Two groups.',
              'Two very different beginnings.',
              'Finally together.',
              'While everyone caught up, Hanna quietly shifted into logistics mode.',
              'She answered questions.',
              'Confirmed our lunch reservation.',
              'Explained how the Blue Lagoon wristbands worked.',
              'Made sure everyone knew where to meet later.',
              'There was nothing flashy about it.',
              'She simply had a way of making everything feel easy.',
              "Only later would we realize how much of the day's schedule had already been quietly rearranged because of our delayed arrival.",
              'Despite losing several hours the night before, nothing felt rushed.',
              'Nothing felt missed.',
              'The day simply flowed.',
            ],
          },
          {
            heading: 'Iceland\'s First Surprise',
            paragraphs: [
              'Before we even reached the entrance, Iceland introduced us to another first.',
              'There was a distinct smell in the air.',
              'Someone laughed.',
              '"Does anyone else smell eggs?"',
              'It turned out everyone did.',
              "The faint sulfur smell drifting across the lava fields was our first real introduction to Iceland's geothermal landscape. It caught almost everyone off guard, but within minutes it became just another part of the experience.",
              'Hanna smiled.',
              '"That\'s Iceland saying hello."',
              'She explained that the sulfur smell comes from naturally occurring geothermal gases that heat homes, fill hot springs, and power much of the country.',
              '"Give it a couple of days," she laughed.',
              '"You won\'t even notice it anymore."',
              'She was right.',
              "By the end of the week, we'd barely noticed it.",
              'Like so many things in Iceland, it quickly became part of the rhythm of the place.',
              'Steam drifted from vents hidden among the lava rocks.',
              'Hot water flowed beneath our feet.',
              "The landscape wasn't just beautiful.",
              'It was alive.',
            ],
          },
          {
            heading: 'The Great Blue Lagoon Scavenger Hunt',
            paragraphs: [
              "If you've never been to the Blue Lagoon, here's something no one tells you.",
              'Getting into the water is surprisingly complicated.',
              'There are lockers.',
              'Electronic wristbands.',
              'Changing rooms.',
              'Showers.',
              'Towels.',
              "Swimsuits stuffed into lockers you'll almost certainly forget the location of five minutes later.",
              'For a group our size, there was a steady stream of questions.',
              '"Wait... which locker was mine?"',
              '"Do I keep the wristband on?"',
              '"Where do the towels go?"',
              '"Is everyone already outside?"',
              'It was organized chaos.',
              "The kind that's funny because everyone is figuring it out together.",
              'Through it all, Hanna calmly moved from person to person.',
              'Helping someone with a wristband.',
              'Pointing another family toward the changing rooms.',
              'Explaining how the lockers worked.',
              'Answering the same question more than once without ever seeming rushed.',
              'Before everyone disappeared into the locker rooms, she gathered us together for one quick reminder.',
              '"Lunch is at 12:30 in Lava Restaurant," she said.',
              '"Take your time. Enjoy the lagoon. We\'ll meet there."',
              'It sounded simple.',
              "Looking back, it wasn't.",
              'Behind the scenes, reservations had been adjusted.',
              'Transportation had been coordinated.',
              "The day's timeline had been reworked.",
              'Yet none of us felt any of it.',
              "That's the mark of a great guide.",
              'The work you notice is rarely as impressive as the work you never see.',
              'Eventually, one by one, we made our way through the maze of locker rooms and stepped outside.',
              'And then we faced a completely different challenge.',
              'Finding everyone.',
            ],
          },
          {
            heading: 'Lost in the Steam',
            paragraphs: [
              'The Blue Lagoon is much larger than it appears in photographs.',
              "What the pictures don't capture is the steam.",
              'Warm geothermal water meeting the cool Icelandic air creates a blanket of mist that constantly drifts across the lagoon.',
              'Some moments you could see across the water.',
              "The next moment you couldn't see more than a few feet in front of you.",
              'Finding the rest of the group became its own little adventure.',
              "You'd spot someone through the steam.",
              'Start walking toward them.',
              'Only to have them disappear again as another cloud rolled across the water.',
              'Then, almost out of nowhere, another familiar face would emerge from the mist.',
              '"Hey! There you are!"',
              'Slowly, everyone found each other.',
              'The conversations picked up right where they had left off in the parking lot.',
              'The team that had arrived the day before shared stories from Reykjavik.',
              'The rest of us retold the saga of our delayed flight - now with enough distance that it had already become more entertaining than frustrating.',
              'It struck me that the steam almost mirrored the trip itself.',
              "At first, you couldn't quite see where everyone was.",
              'But eventually, one by one, everyone found each other.',
              'And once we did, it felt like the retreat could finally begin.',
            ],
          },
          {
            heading: 'That First Step',
            paragraphs: [
              'The walk from the building to the water was brief, but unforgettable.',
              'Cool Icelandic air surrounded us as we made our way toward the lagoon.',
              'Then came the first step into the water.',
              'Warm.',
              'Silky.',
              'Completely unexpected.',
              'After nearly twenty-four hours of airports, airplanes, buses, and rental cars, it felt like every tense muscle finally relaxed.',
              'People instinctively smiled.',
              'Shoulders dropped.',
              'Someone simply said,',
              '"This was worth the trip."',
              'It was hard to argue.',
              'The contrast between the cool morning air and the warm geothermal water was unlike anything most of us had ever experienced.',
              'Steam drifted across the surface.',
              'Black lava fields surrounded the lagoon.',
              'The sky seemed impossibly wide.',
              'The stress of getting there quietly melted away.',
            ],
          },
          {
            heading: 'Where the Retreat Really Began',
            paragraphs: [
              "Looking back, I don't think the retreat officially began when we landed in Iceland.",
              'I think it began here.',
              "Floating in warm geothermal water, there wasn't one large group.",
              'There were dozens of little conversations.',
              'Coworkers drifted from one group to another.',
              'Spouses shared stories.',
              'Kids discovered the silica mud stations long before the adults did.',
              "People compared notes from the previous day's adventures while others retold the story of the airport delay for what was probably the fifth time.",
              'Nobody seemed to mind.',
              'There was no agenda.',
              'No presentations.',
              'No meetings.',
              'No one was checking Slack.',
              'No one was looking at a watch.',
              'Everyone was simply present.',
              "It reminded me that culture isn't built during scheduled activities.",
              "It's built in moments exactly like this.",
            ],
          },
          {
            heading: 'Looking a Little Ridiculous',
            paragraphs: [
              'Of course, no visit to the Blue Lagoon is complete without trying the famous silica mud mask.',
              'Within minutes, everyone was wearing bright white silica mud masks.',
              'Adults.',
              'Kids.',
              'Parents.',
              'Coworkers.',
              'Everyone looked equally ridiculous.',
              'And everyone laughed.',
              "It's funny how quickly the labels we carry every day disappear.",
              'Nobody cared what anyone did for work.',
              "We weren't developers.",
              "We weren't project managers.",
              "We weren't business owners.",
              "We were simply a group of families sharing an experience we'd never forget.",
              'Those are the moments that stay with you long after the trip is over.',
            ],
          },
          {
            heading: 'Lunch at Lava',
            paragraphs: [
              'At 12:30, just as Hanna had promised, everyone gradually found their way to Lava Restaurant.',
              "Built into the volcanic landscape overlooking the Blue Lagoon, the restaurant felt like a natural extension of the morning. Floor-to-ceiling windows looked out across the steam rising from the lagoon we'd just left behind, making it easy to linger a little longer before the next adventure.",
              "The meal reflected the same philosophy we'd already begun to appreciate throughout Iceland: let exceptional ingredients speak for themselves.",
              "Lunch began with Arctic char, delicately prepared with cucumber, horseradish, and yuzu. Light, fresh, and unmistakably Nordic, it was the perfect introduction to Iceland's coastal cuisine.",
              'The main course featured beautifully grilled beef accompanied by mushrooms, artichokes, and leeks. It was comforting without being heavy - simple ingredients prepared with remarkable care.',
              'Dessert celebrated Icelandic tradition with Astarpungar, a beloved Icelandic pastry, served alongside mango chocolate mousse, vanilla ice cream, and salted caramel. It was a fitting finish to our first meal together in Iceland - equal parts familiar and unexpected.',
              "Like everything we'd experienced so far, the meal wasn't about excess.",
              'It was thoughtful.',
              'Intentional.',
              'Comforting.',
              'As lunch stretched on, conversations naturally shifted toward what lay ahead.',
              "Tomorrow we'd be riding snowmobiles across Langjokull Glacier.",
              'Exploring an ice cave.',
              "Experiencing landscapes we'd only seen in photographs.",
              'The excitement was building.',
              'As everyone finished dessert, Hanna gathered us together once more.',
              "The afternoon's drive would take us deeper into Iceland, toward the place we'd call home for the next two nights.",
              'Before we climbed back into the vehicles, she offered one simple piece of advice.',
              '"Keep looking out the windows."',
              '"The drive is part of the experience."',
              'She was absolutely right.',
            ],
          },
          {
            heading: 'Why We Started Here',
            paragraphs: [
              'People have asked why we chose the Blue Lagoon as the very first stop of the retreat.',
              'The answer has very little to do with the Blue Lagoon itself.',
              "After everything it had taken just to reach Iceland, we didn't need another activity.",
              'We needed a transition.',
              'A place where the stress of travel could quietly disappear.',
              'A place where families could reconnect after a long journey.',
              'A place where conversations could begin naturally.',
              'A place where everyone could arrive - not just physically, but mentally.',
              'Looking back, I also realized something else.',
              "The Blue Lagoon wasn't the first time our plans changed.",
              'It was simply the first time we realized they could.',
              'Throughout the week, weather would shift.',
              'Timelines would move.',
              'Routes would change.',
              'And somehow, everything still worked.',
              'Not because every detail went according to plan.',
              'But because we had people around us who knew how to adapt.',
              "Sometimes the best travel experiences aren't the ones that unfold exactly as you imagined.",
              "They're the ones made possible by people quietly adjusting behind the scenes so you never have to think about it.",
              "Looking back, I don't think we could have chosen a better place to begin.",
              'Somewhere between the steam rising from the lava fields and the warmth of the geothermal water surrounding us, the airport disappeared.',
              'The delays were forgotten.',
              'The uncertainty was behind us.',
              'Iceland no longer felt like a destination we had traveled to.',
              'It felt like home for the week ahead.',
            ],
          },
          {
            heading: 'Next in the Series: Home Among the Turf Houses',
            paragraphs: [
              "Leaving the Blue Lagoon behind, we headed east through Iceland's dramatic countryside toward Torfhus Retreat. More than just a place to stay, it became the heart of our week - a place where Viking-inspired architecture, private geothermal hot tubs, incredible meals, and unhurried conversations reminded us that sometimes the best part of the journey is simply having a place to gather.",
            ],
          },
        ],
      },
      {
        eyebrow: 'Travel Blog',
        title: 'Racing Across a Glacier',
        released: false,
        lede: 'Sometimes the best adventures begin with "I\'ve never done this before."',
        sections: [
          {
            heading: 'A First for Almost Everyone',
            paragraphs: [
              'The morning began with a mixture of excitement and nervous energy.',
              'Most of us had never driven a snowmobile before.',
              "We had seen the pictures. We knew we would be riding across Langjokull, Iceland's second-largest glacier. But until you stand there with your helmet in hand, staring out at miles of untouched snow stretching toward the horizon, it is impossible to appreciate what you are about to experience.",
              'The glacier has a way of making you feel incredibly small.',
              'And that is part of its magic.',
            ],
          },
          {
            heading: 'Learning Something New',
            paragraphs: [
              'There was a moment before we climbed onto the machines when everyone looked around with the same expression.',
              '"I hope I do not mess this up."',
              'Our guides walked us through the basics.',
              'How to steer.',
              'How to brake.',
              'How to lean.',
              'Simple enough.',
              'At least in theory.',
              'The first few minutes were cautious. We eased onto the glacier in a single line, getting comfortable with the machines and learning how they responded on the snow.',
              'Then something happened.',
              'Confidence replaced hesitation.',
              'The throttle opened just a little more.',
              'The smiles got a little bigger.',
              'And suddenly we were not thinking about driving anymore.',
              'We were simply enjoying the ride.',
            ],
          },
          {
            heading: 'A Landscape Unlike Anywhere Else',
            paragraphs: [
              'Snowmobiles have a way of making you feel like you are flying.',
              'There are no lanes.',
              'No stoplights.',
              'No buildings.',
              'Just an endless blanket of snow beneath a sky that seems to stretch forever.',
              'The only sounds were the engines, the wind, and the occasional laugh that somehow carried across the glacier.',
              'Every few miles we would stop.',
              'Helmets came off.',
              'People looked around in complete disbelief.',
              'Photos were taken, but they never quite captured the scale.',
              'Some places simply have to be experienced.',
            ],
          },
          {
            heading: "The Blizzard We Didn't Expect",
            paragraphs: [
              'As if Iceland wanted to remind us who was really in charge, the weather changed.',
              'One moment visibility stretched for miles.',
              'The next, snow whipped across the glacier, turning the landscape into a world of white.',
              'It was not frightening.',
              'It was exhilarating.',
              'The guides calmly led the way while our convoy pressed on through conditions that felt straight out of an expedition documentary.',
              "Ironically, the blizzard became one of everyone's favorite memories.",
              'Sometimes the unexpected becomes the highlight.',
            ],
          },
          {
            heading: 'Standing Inside a Glacier',
            paragraphs: [
              'Our destination was not just the glacier itself.',
              'Hidden beneath its surface was something even more remarkable.',
              'A natural ice cave.',
              "Every year the glacier creates new caves through a constant cycle of freezing, melting, and shifting ice, making each season's cave unique. Stepping inside felt almost surreal. The walls shimmered with deep shades of blue that seemed to glow from within, reminding us that nature can create beauty no architect could ever replicate.",
              'Conversation almost stopped.',
              'Everyone simply looked around.',
              'Some experiences do not need narration.',
            ],
          },
          {
            heading: 'Why We Do Things Like This',
            paragraphs: [
              'People often ask why we invest in experiences like these.',
              'The answer is not snowmobiles.',
              'It is not glaciers.',
              'It is not even Iceland.',
              'It is what happens because of them.',
              'When everyone is trying something for the first time, titles disappear.',
              'Nobody is the expert.',
              'People encourage one another.',
              'They celebrate small victories.',
              'They laugh when things do not go exactly as planned.',
              'Those moments build trust in ways that conference rooms never can.',
            ],
          },
          {
            heading: 'Looking Back',
            paragraphs: [
              'By the time we returned to base camp, none of us were the same people who had nervously climbed onto those snowmobiles a few hours earlier.',
              'We were more confident.',
              'A little colder.',
              'A lot happier.',
              'And we all shared a story we will be telling for years.',
              'It is easy to think the highlight of Iceland is the scenery.',
              'For us, it was not.',
              'It was sharing the adventure with people who made every mile across that glacier unforgettable.',
            ],
          },
          {
            heading: 'Next Up',
            paragraphs: [
              'After an unforgettable morning on the glacier, we traded adrenaline for serenity with an afternoon at Laugaras Lagoon, where warm geothermal waters, incredible food, and great conversation proved to be the perfect ending to an extraordinary day.',
            ],
          },
        ],
      },
      {
        eyebrow: 'Travel Blog',
        title: 'From Adrenaline to Tranquility',
        released: false,
        lede: 'How Laugaras Lagoon became the perfect ending to an unforgettable day.',
        sections: [
          {
            heading: 'Slowing Down After the Glacier',
            paragraphs: [
              'There are days when you remember every detail.',
              'The roar of a snowmobile engine.',
              'The crunch of fresh snow beneath your boots.',
              'The brilliant blue walls of an ice cave hidden beneath a glacier.',
              "Day Two of our Iceland adventure was full of moments like those. By the afternoon, we had crossed one of Iceland's largest glaciers, experienced a surprise blizzard, and checked off a bucket-list adventure that many of us had only dreamed about.",
              'Then we did something that made the day even better.',
              'We slowed down.',
            ],
          },
          {
            heading: 'The Perfect Contrast',
            paragraphs: [
              'One of the things Iceland does exceptionally well is balance extremes.',
              'Fire and ice.',
              'Volcanoes and glaciers.',
              'Adventure and tranquility.',
              "After a morning fueled by adrenaline, we arrived at Laugaras Lagoon, tucked along the banks of the Hvita River in the heart of Iceland's Golden Circle. Surrounded by mountains and flowing water, the lagoon feels like it belongs exactly where it is, blending naturally into the landscape instead of competing with it.",
              "It wasn't just another stop on the itinerary.",
              'It was exactly what we needed.',
            ],
          },
          {
            heading: 'Letting the Day Sink In',
            paragraphs: [
              'The first step into the warm geothermal water felt familiar after our visit to the Blue Lagoon, but the atmosphere at Laugaras was completely different.',
              'Smaller.',
              'Quieter.',
              'More intimate.',
              'As steam drifted across the surface, conversations picked up where they had left off earlier in the day.',
              '"Can you believe that blizzard?"',
              '"I still can\'t get over that ice cave."',
              '"I\'ve never done anything like that before."',
              'The glacier had given us something to talk about.',
              'The lagoon gave us the time to enjoy those conversations.',
              'Sometimes that is what makes an experience memorable. Not the activity itself, but the opportunity to reflect on it together.',
            ],
          },
          {
            heading: 'Warm Water, Cold Water, New Perspectives',
            paragraphs: [
              'Some members of the group embraced every part of the experience.',
              'Warm pools.',
              'Cold plunges.',
              'Saunas overlooking the river.',
              'Others were perfectly content to find a comfortable spot in the geothermal water and simply watch the afternoon unfold.',
              'There was no schedule.',
              'No rush.',
              'No expectation that everyone had to experience the lagoon the same way.',
              'And maybe that is one of the reasons it worked so well.',
              'Everyone found their own way to recharge.',
            ],
          },
          {
            heading: 'A Place That Encourages Conversation',
            paragraphs: [
              'One thing we have noticed throughout this retreat is that some of the best conversations happen when there is nowhere else to be.',
              'No one was checking the time.',
              'No one was looking at their phone.',
              'The setting naturally encouraged people to linger.',
              'Coworkers shared stories from earlier in the day.',
              'Families talked about the adventures still ahead.',
              'Kids laughed as they moved between the warm water and cooler air without seeming to notice the temperature.',
              'It is amazing how quickly genuine connections grow when people have the chance to simply spend time together.',
            ],
          },
          {
            heading: 'Dinner at Ylja',
            paragraphs: [
              "As evening approached, we made our way to Ylja, the lagoon's restaurant.",
              'The name Ylja means warmth in Icelandic, and it could not have been more fitting.',
              'Warmth from the geothermal waters.',
              'Warmth from the incredible food.',
              'And perhaps most importantly, the warmth of sharing a meal with people who had just experienced something extraordinary together.',
              "Led by renowned Icelandic chef Gisli Matt, the restaurant celebrates the country's remarkable ingredients. Fresh seafood, produce grown in geothermal greenhouses, and locally sourced ingredients come together in a menu that reflects Iceland's deep connection to the land.",
              'Every course told a story.',
              'Not through complexity.',
              'Through simplicity.',
              'The ingredients did not need to be hidden behind elaborate techniques.',
              'They were allowed to speak for themselves.',
            ],
          },
          {
            heading: 'Meals Are Where Memories Settle In',
            paragraphs: [
              'If adventure creates stories, meals are where those stories come to life.',
              'By dinner, everyone had their favorite moment from the glacier.',
              'Someone could not stop talking about the endless views.',
              'Someone else laughed about learning to drive a snowmobile.',
              'Others were still trying to find the right words to describe standing inside a natural ice cave.',
              "The conversations flowed as naturally as the river outside the restaurant's windows.",
              'By now, we were not just talking about what we had done.',
              'We were talking about how it made us feel.',
              'That is when you realize an adventure has become something more than an itinerary.',
              'It has become a shared memory.',
            ],
          },
          {
            heading: 'Why We Plan Retreats This Way',
            paragraphs: [
              'People sometimes assume a company retreat is about the activities.',
              'The glacier.',
              'The lagoon.',
              'The restaurants.',
              'Those experiences matter.',
              'But they are not the reason we travel.',
              'The real purpose is creating opportunities for connection.',
              'Adventure opens the door.',
              'Relaxation gives people the space to walk through it.',
              'Some of the strongest relationships are not built while racing across a glacier.',
              'They are built afterward, sitting in warm water or around a dinner table, reflecting on the day together.',
              'That is where trust grows.',
              'That is where friendships deepen.',
              'And that is where company culture becomes something more than words on a website.',
            ],
          },
          {
            heading: 'Ending the Day Right',
            paragraphs: [
              'As we left Laugaras Lagoon that evening, the excitement of the morning had settled into something quieter.',
              'Gratitude.',
              'Not just for the places we had visited, but for the people we had shared them with.',
              'The glacier had challenged us.',
              'The lagoon had restored us.',
              'Together, they captured something that is uniquely Icelandic.',
              'Adventure and peace do not have to compete.',
              'Sometimes they make each other even better.',
            ],
          },
          {
            heading: 'Next in the Series',
            paragraphs: [
              "Tomorrow, we will trade guided adventures for the driver's seat as we climb behind the wheel of Super Jeeps, explore Iceland's rugged backroads, stop for a wilderness picnic in one of the most beautiful places we have ever shared a meal, and make our way to Reykjavik for the next chapter of our journey.",
            ],
          },
        ],
      },
    ],
    media: [
      {
        type: 'image',
        title: 'Early Arrival in Iceland',
        caption: 'The early-arrival crew made it to Iceland a day ahead of the main group.',
        src: mediaRef('adventures/iceland-blog/iceland-early-arrival-airport.jpg'),
        alt: 'Members of the Reckon Crew arriving early at Keflavík Airport',
        aspectRatio: 1.5,
      },
      {
        type: 'image',
        title: 'First Reykjavík Lunch',
        caption: 'A first meal in Reykjavík while the rest of the crew waited at Gate A24.',
        src: mediaRef('adventures/iceland-blog/iceland-early-arrival-lunch.jpg'),
        position: 'center top',
        alt: 'Two Reckon Crew members sharing lunch and drinks in Reykjavík',
        aspectRatio: 0.67,
      },
      {
        type: 'image',
        title: 'The First Table in Iceland',
        caption: 'The first Iceland stories were already taking shape around the table.',
        src: mediaRef('adventures/iceland-blog/iceland-early-arrival-group-lunch.jpg'),
        alt: 'The early-arrival Reckon group gathered around a lunch table in Reykjavík',
        aspectRatio: 1.5,
      },
      {
        type: 'image',
        title: 'Exploring Reykjavík',
        caption: 'Exploring Reykjavík together before the retreat officially began.',
        src: mediaRef('adventures/iceland-blog/iceland-early-arrival-bastard-bar.jpg'),
        alt: 'The early-arrival group outside Bastard Brew and Food in Reykjavík',
        aspectRatio: 0.67,
      },
      {
        type: 'image',
        title: 'A Reykjavík Toast',
        caption: 'A Reykjavík toast shared back to the crew still waiting in Washington.',
        src: mediaRef('adventures/iceland-blog/iceland-early-arrival-pub-toast.jpg'),
        alt: 'Three members of the Reckon Crew raising drinks in a Reykjavík pub',
        aspectRatio: 1.5,
      },
    ],
  },
  {
    slug: 'xcaret-2025',
    title: 'Xcaret Mexico 2025',
    meta: 'Corporate Retreat',
    description: 'A family-inclusive Riviera Maya retreat with Xcaret adventures, catamaran sailing to Isla Mujeres, snorkeling, cenotes, and team dinners.',
    tags: tagRefs(
      'corporate-retreat',
      'family-friendly',
      'international',
      'mexico',
      'adventure',
      'beach',
      'resort',
      'snorkeling',
      'sailing',
      'catamaran',
      'cenotes',
      'water-adventures',
      'family-adventures',
      'reckon-retreats',
    ),
    achievements: achievementRefs(
      'catamaran-sailing',
      'coba-and-cenotes',
    ),
    firsts: firstRefs(
      'first-cenote',
      'first-catamaran',
    ),
    familyFriendly: true,
    familyNote: 'Loved ones were invited to join this adventure.',
    experienceDNA: {
      food: 3,
      adventure: 5,
      family: 5,
      nature: 4,
      culture: 2,
      relaxation: 4,
      luxury: 3,
    },
    coverImage: mediaRef('adventures/xcaret-2025.png'),
    coverAspectRatio: 1.5,
    heroImage: mediaRef('adventures/xcaret-heroes-no-text.png'),
    tripCard: {
      tripSlug: 'xcaret-corporate-retreat-2025',
      title: 'Xcaret Corporate Retreat',
      subtitle: 'Adventure, Connection, and the Riviera Maya',
      destination: 'Riviera Maya, Mexico',
      date: 'April 2025',
      tripType: 'Corporate Retreat',
      heroImage: mediaRef('adventures/xcaret-2025.png'),
      heroAspectRatio: 1.5,
      badgeIcon: '🇲🇽',
      familyIncluded: true,
      familyFriendly: true,
      familyNote: 'Loved ones were invited to join this adventure.',
      featuredActivities: [
        'Exploring Xcaret',
        'Catamaran Sailing to Isla Mujeres',
        'Snorkeling',
        'Cenotes',
        'Team Dinners',
      ],
      featuredMeals: [
        'Group dinners',
        'Local favorites',
        'Resort dining',
      ],
      earnedBadges: [
        'Catamaran Sailing',
        'Exploring Coba',
        'Cenotes',
      ],
      summary: 'A family-inclusive Riviera Maya retreat that balanced team connection with a new adventure each day. The Crew settled in over dinner at Las Playas, sailed by catamaran to Isla Mujeres, gathered for an all-hands before exploring Xcaret, toured Cobá and the cenotes, and closed the trip with a yacht outing and dinner at Ha.',
      ctaText: 'View Adventure',
      ctaUrl: '/the-reckon-way/xcaret-2025',
    },
    journeyDays: [
      {
        id: 'day-1-april-11', label: 'Day 1 · April 11, 2025', value: 'Flight, Hotel Check-In & Dinner at Las Playas', status: 'coming-soon',
      },
      {
        id: 'day-2-april-12', label: 'Day 2 · April 12, 2025', value: 'Isla Mujeres Catamaran & Dinner at Las Cuevas', status: 'coming-soon',
      },
      {
        id: 'day-3-april-13', label: 'Day 3 · April 13, 2025', value: 'All-Hands, Explore Xcaret & Dinner at Playón Espiral', status: 'coming-soon',
      },
      {
        id: 'day-4-april-14', label: 'Day 4 · April 14, 2025', value: 'Cobá & Cenotes Tour', status: 'coming-soon',
      },
      {
        id: 'day-5-april-15', label: 'Day 5 · April 15, 2025', value: 'Yacht & Dinner at Ha', status: 'coming-soon',
      },
    ],
    storiesComingSoon: true,
    storyComingSoonTitle: 'Xcaret Stories Coming Soon',
    storyComingSoonCopy: 'The trip page is open for the adventure overview and photo gallery. Longer stories from Xcaret will be added as the content is ready.',
    blog: createTravelBlog('Xcaret Mexico 2025'),
    media: [],
  },
  {
    slug: 'bahamas-2024',
    title: 'Miami & Bahamas',
    meta: 'Corporate Retreat',
    description: 'A night and morning all-hands in Miami followed by a three-day Norwegian cruise, with stops at Atlantis and the cruise line’s private cay.',
    destination: 'Miami, Nassau & Great Stirrup Cay',
    destinationStatLabel: 'Destinations',
    tags: tagRefs(
      'adventure',
      'family-friendly',
      'international',
      'bahamas',
      'beach',
      'resort',
      'water-park',
      'city-exploration',
      'water-adventures',
      'family-adventures',
    ),
    achievements: achievementRefs('exploring-atlantis'),
    firsts: firstRefs('first-atlantis'),
    familyFriendly: true,
    familyNote: 'Loved ones were invited to join this adventure.',
    experienceDNA: {
      food: 4,
      adventure: 4,
      family: 5,
      nightlife: 3,
      nature: 3,
      culture: 1,
      relaxation: 4,
      luxury: 3,
    },
    coverImage: mediaRef('adventures/bahamas-2024.png'),
    coverAspectRatio: 1.5,
    heroImage: mediaRef('adventures/bahamas-heroes-no-text.png'),
    tripCard: {
      tripSlug: 'bahamas-atlantis-2024',
      title: 'Atlantis Paradise Island',
      subtitle: 'From Miami Rooftops to Bahamian Shores',
      destination: 'Miami, Nassau & Great Stirrup Cay',
      date: 'May 2024',
      tripType: 'Corporate Retreat',
      heroImage: mediaRef('adventures/bahamas-2024.png'),
      heroAspectRatio: 1.5,
      badgeIcon: '🇧🇸',
      familyIncluded: true,
      familyFriendly: true,
      familyNote: 'Loved ones were invited to join this adventure.',
      featuredActivities: [
        'Moxy Rooftop All-Hands',
        'Crew Portraits by Jon Meadows',
        'Cruising aboard the Norwegian Sky',
        'Exploring Nassau',
        'Atlantis Paradise Island',
        'Great Stirrup Cay',
      ],
      featuredMeals: [
        "Joe's Stone Crab",
        'Dining aboard the Norwegian Sky',
      ],
      earnedBadges: [
        'Exploring Atlantis',
      ],
      summary: 'Reckon’s first attempt at a corporate retreat began in Miami with a South Beach stay, dinner at Joe’s Stone Crab, a rooftop all-hands, and crew portraits. From there, the Crew boarded the Norwegian Sky for Nassau and Great Stirrup Cay before returning to Miami for the flight home.',
      ctaText: 'View Adventure',
      ctaUrl: '/the-reckon-way/bahamas-2024',
    },
    journeyDays: [
      {
        id: 'day-1-april-30', label: 'Day 1 · April 30, 2024', value: 'Miami Arrival', status: 'coming-soon',
      },
      {
        id: 'day-2-may-1', label: 'Day 2 · May 1, 2024', value: 'Miami', status: 'coming-soon',
      },
      {
        id: 'day-3-may-2', label: 'Day 3 · May 2, 2024', value: 'Miami & Embarkation', status: 'coming-soon',
      },
      {
        id: 'day-4-may-3', label: 'Day 4 · May 3, 2024', value: 'Nassau', status: 'coming-soon',
      },
      {
        id: 'day-5-may-4', label: 'Day 5 · May 4, 2024', value: 'Great Stirrup Cay', status: 'coming-soon',
      },
      {
        id: 'day-6-may-5', label: 'Day 6 · May 5, 2024', value: 'Miami & Travel Home', status: 'coming-soon',
      },
    ],
    storiesComingSoon: true,
    storyComingSoonTitle: 'Bahamas Stories Coming Soon',
    storyComingSoonCopy: 'The trip page is open for the adventure overview and photo gallery. Longer stories from Atlantis Paradise Island will be added as the content is ready.',
    blog: createTravelBlog('Bahamas 2024'),
    media: [],
  },
];

export const conferenceGalleries = [
  {
    slug: 'sxsw-sydney-2025',
    legacySlugs: ['sxsw-sydney'],
    title: 'SXSW Sydney 2025',
    subtitle: 'SXSW brought us to Sydney. The city made the trip unforgettable.',
    meta: 'Conference',
    description: 'SXSW brought us to Sydney. The city made the trip unforgettable.',
    destination: {
      city: 'Sydney',
      region: 'New South Wales',
      country: 'Australia',
      continent: 'Oceania',
    },
    dateLabel: 'October 13–19, 2025',
    startDate: '2025-10-13',
    endDate: '2025-10-19',
    tripType: 'Conference',
    summary: "A conference trip filled with live music, unforgettable food, ferry rides, coastal walks, and time together in one of the world's great cities.",
    tags: tagRefs(
      'conference',
      'international',
      'australia',
      'sydney',
      'live-music',
      'fine-dining',
      'craft-beer',
      'walking',
      'ferry',
      'coastal',
      'urban',
      'crew-favorite',
      'first-time',
      'hidden-gem',
      'conference-series',
      'australia-collection',
      'beer-adventures',
      'best-restaurants',
      'water-adventures',
      'great-walks',
    ),
    achievements: achievementRefs(
      'ferry-to-manly-beach',
      'bondi-to-coogee-coastal-walk',
      'scenic-world-blue-mountains',
    ),
    firsts: firstRefs(
      'first-trip-to-australia',
      'first-dim-sum',
      'first-coat-of-arms-pizza',
    ),
    attendees: tripAttendees(
      {
        employeeSlug: 'kevin-l',
        displayName: 'Kevin L.',
        role: 'Chief Executive Officer',
      },
      {
        employeeSlug: 'brandon-a',
        displayName: 'Brandon A.',
        role: 'Co-Founder',
      },
      {
        employeeSlug: 'brandon-l',
        displayName: 'Brandon L.',
        role: 'Co-Founder',
      },
      {
        employeeSlug: 'matt-g',
        displayName: 'Matt G.',
        role: 'Software Developer',
      },
      {
        employeeSlug: 'ben-b',
        displayName: 'Ben B.',
        role: 'Software Developer',
      },
      {
        employeeSlug: 'phillip-h',
        displayName: 'Phillip H.',
        role: 'Software Developer',
      },
    ),
    experienceDNA: {
      conference: 5,
      food: 5,
      adventure: 3,
      family: 0,
      nightlife: 5,
      nature: 3,
      culture: 4,
      relaxation: 2,
      luxury: 3,
    },
    coverImage: mediaRef('adventures/sxsw-sydney.png'),
    heroImage: mediaRef('adventures/sxsw-sydney-heroes-no-text.png'),
    inlineBlog: true,
    blog: sxswSydneyBlog,
    media: [
      {
        type: 'image',
        title: 'Manly Beach Crew',
        caption: 'The crew at Manly Beach after the ferry ride across Sydney Harbour.',
        src: mediaRef('adventures/sydney-manly-beach-crew-selfie.jpg'),
        alt: 'Reckon crew taking a selfie at Manly Beach in Sydney',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Coogee to Bondi - Sculpture by the Sea',
        caption: 'Sculpture by the Sea turned the coastal walk into an outdoor gallery.',
        src: mediaRef('adventures/sydney-bondi-coogee-sculpture-tree.jpg'),
        alt: 'Sculpture by the Sea tree installation overlooking the Bondi coastline',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Coogee to Bondi - Coastal Sculpture',
        caption: 'A coastal sculpture installation overlooking the Pacific.',
        src: mediaRef('adventures/sydney-bondi-coogee-sculpture-chair.jpg'),
        alt: 'Sculpture by the Sea chair installation on the rocks above the ocean',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Coogee to Bondi - Sculpture Trail',
        caption: 'Art installations appeared around each bend of the trail.',
        src: mediaRef('adventures/sydney-bondi-coogee-sculpture-seal.jpg'),
        alt: 'Sculpture by the Sea seal installation along the Coogee to Bondi walk',
        aspectRatio: 0.75,
      },
      {
        type: 'image',
        title: 'Bondi Beach from the Coastal Walk',
        caption: 'Bondi Beach came into view as we made the final stretch of the walk.',
        src: mediaRef('adventures/sydney-bondi-coogee-bondi-beach-flowers.jpg'),
        alt: 'Bondi Beach coastline with orange flowers in the foreground',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Coogee to Bondi Coastline',
        caption: 'Rocky cliffs, ocean views, and sculpture installations along the trail.',
        src: mediaRef('adventures/sydney-bondi-coogee-coastline.jpg'),
        alt: 'Rocky coastline and surf along the Coogee to Bondi coastal walk',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Coogee to Bondi - Painter Sculpture',
        caption: 'A playful Sculpture by the Sea installation along the walk.',
        src: mediaRef('adventures/sydney-bondi-coogee-sculpture-painter.jpg'),
        alt: 'Playful painter sculpture along the Coogee to Bondi walk',
        aspectRatio: 0.75,
      },
      {
        type: 'image',
        title: 'Bondi Beach Overlook',
        caption: 'Bondi Beach from above as we finished the Coogee-to-Bondi walk.',
        src: mediaRef('adventures/sydney-bondi-coogee-bondi-beach.jpg'),
        alt: 'Bondi Beach viewed from above with purple flowers in the foreground',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Bondi Trattoria',
        caption: 'The first team meal in Sydney at Bondi Trattoria.',
        src: mediaRef('adventures/sydney-bondi-trattoria.jpg'),
        alt: 'Reckon crew gathered around a table at Bondi Trattoria in Sydney',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: '20 Chapel Team Dinner',
        caption: 'The team dinner at 20 Chapel, the unanimous group favorite.',
        src: mediaRef('adventures/sydney-20-chapel-team-dinner.jpg'),
        alt: 'Reckon crew gathered around the table for dinner at 20 Chapel in Sydney',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: '4 Pines Brew Pub - Manly',
        caption: 'A well-earned stop at 4 Pines after the ferry ride to Manly.',
        src: mediaRef('adventures/sydney-4-pines-team-lunch.jpg'),
        alt: 'Reckon crew gathered around a table at 4 Pines Brew Pub in Manly',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Mountain Culture Beer Co.',
        caption: 'A pint of Status Quo, their award-winning hazy New England-style pale ale. Unfortunately its the only photo from an unforgettable brewery.',
        src: mediaRef('adventures/sydney-mountain-culture-beer.jpg'),
        alt: 'A beer on the table at Mountain Culture Beer Co. in the Blue Mountains',
        aspectRatio: 0.75,
      },
      {
        type: 'image',
        title: 'Sydney Flat White',
        caption: 'A Sydney flat white, because Australians take their coffee seriously and flat whites absolutely live up to the hype.',
        src: mediaRef('adventures/sydney-flat-white.jpg'),
        alt: 'A flat white coffee with latte art in Sydney',
        aspectRatio: 0.75,
      },
      {
        type: 'image',
        title: 'Margaret Team Dinner',
        caption: 'Team dinner at Margaret, one of the world\'s best steakhouses.',
        src: mediaRef('adventures/sydney-margaret-team-dinner.jpg'),
        alt: 'Reckon crew seated outside at Margaret for dinner in Sydney',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Quay Opera House View',
        caption: 'The Opera House view from dinner at Quay.',
        src: mediaRef('adventures/sydney-quay-opera-house-view.jpg'),
        alt: 'Wine bottles in front of the Sydney Opera House view from Quay',
        aspectRatio: 0.75,
      },
      {
        type: 'image',
        title: 'Quay Team Dinner',
        caption: 'The crew gathered at Quay with Sydney Harbour behind them.',
        src: mediaRef('adventures/sydney-quay-team-dinner.jpg'),
        alt: 'Reckon crew seated together at Quay with the Sydney Opera House behind them',
        aspectRatio: 0.75,
      },
      {
        type: 'image',
        title: 'Australian Heritage Hotel',
        caption: 'Coat of Arms Pizza at the Australian Heritage Hotel.',
        src: mediaRef('adventures/sydney-australian-heritage-hotel.jpg'),
        alt: 'Reckon crew sharing Coat of Arms Pizza at the Australian Heritage Hotel',
        aspectRatio: 1.33,
      },
      {
        type: 'image',
        title: 'Sydney Rooftop Pub',
        caption: 'A rooftop pub stop with the Sydney Opera House glowing across the harbour.',
        src: mediaRef('adventures/sydney-pubs-opera-house-rooftop.jpg'),
        alt: 'Reckon crew at a rooftop pub with the Sydney Opera House glowing in the background',
        aspectRatio: 0.56,
      },
      {
        type: 'image',
        title: 'Sydney Pub Table',
        caption: 'Another night, another pub table, and another round of Sydney stories.',
        src: mediaRef('adventures/sydney-pubs-outdoor-table.jpg'),
        alt: 'Reckon crew gathered around an outdoor pub table in Sydney',
        aspectRatio: 1.78,
      },
      {
        type: 'image',
        title: 'Sydney Steak Dinner',
        caption: 'A beautifully prepared steak during one of the crew\'s Sydney dinners.',
        src: mediaRef('adventures/sydney-2025-img-5008.jpg'),
        alt: 'Sliced bone-in steak served during a Reckon crew dinner in Sydney',
        aspectRatio: 0.75,
        capturedAt: '2025-10-15T01:53:28+11:00',
      },
      {
        type: 'image',
        title: 'Opera House at Night',
        caption: 'Sydney Harbour and the Opera House glowing after dark.',
        src: mediaRef('adventures/sydney-2025-img-5184.jpg'),
        alt: 'Sydney Opera House and city skyline illuminated across the harbour at night',
        aspectRatio: 1.33,
        capturedAt: '2025-10-16T00:00:12+11:00',
      },
      {
        type: 'image',
        title: 'Harbour Bridge at Night',
        caption: 'The Sydney Harbour Bridge framed by the city lights.',
        src: mediaRef('adventures/sydney-2025-img-5190.jpg'),
        alt: 'Sydney Harbour Bridge illuminated over the water at night',
        aspectRatio: 1.33,
        capturedAt: '2025-10-16T00:01:59+11:00',
      },
      {
        type: 'image',
        title: 'Opera House Across the Harbour',
        caption: 'One more nighttime view of Sydney\'s unmistakable waterfront.',
        src: mediaRef('adventures/sydney-2025-img-5194.jpg'),
        alt: 'Sydney Opera House illuminated across the harbour at night',
        aspectRatio: 1.33,
        capturedAt: '2025-10-16T00:02:36+11:00',
      },
      {
        id: 'sydney-video-img-5198',
        type: 'video',
        title: 'Sydney Harbour at Night',
        caption: 'A moving view of Sydney Harbour during the crew\'s evening exploration.',
        src: mediaRef('adventures/sydney-2025-img-5198.mp4', {
          firebaseAssetId: 'sydney-video-img-5198',
        }),
        alt: 'Video from the Reckon crew exploring Sydney Harbour at night',
        aspectRatio: 0.56,
        capturedAt: '2025-10-16T00:03:00+11:00',
      },
      {
        type: 'image',
        title: 'Morning on the Sydney Coast',
        caption: 'Morning light over a quiet beach along Sydney\'s coastline.',
        src: mediaRef('adventures/sydney-2025-photo-1283.jpg'),
        alt: 'Sunrise over a quiet beach and rocky headland along the Sydney coast',
        aspectRatio: 0.75,
        capturedAt: '2025-10-16T11:05:21+11:00',
      },
      {
        type: 'image',
        title: 'Coastal Morning Light',
        caption: 'The still water and early light made the coast feel miles from the conference.',
        src: mediaRef('adventures/sydney-2025-photo-1304.jpg'),
        alt: 'Morning sun reflected across a calm beach on the Sydney coast',
        aspectRatio: 0.75,
        capturedAt: '2025-10-16T11:05:23+11:00',
      },
      {
        type: 'image',
        title: 'Sydney Coastal Cove',
        caption: 'A sheltered cove discovered while following the coastline.',
        src: mediaRef('adventures/sydney-2025-photo-1383.jpg'),
        alt: 'Sheltered beach and rocky cove along the Sydney coastline',
        aspectRatio: 0.75,
        capturedAt: '2025-10-16T11:06:07+11:00',
      },
      {
        type: 'image',
        title: 'Blue Water Along the Coast',
        caption: 'Clear water, rocky headlands, and another memorable Sydney view.',
        src: mediaRef('adventures/sydney-2025-photo-1451.jpg'),
        alt: 'Clear blue water between rocky headlands on the Sydney coast',
        aspectRatio: 0.75,
        capturedAt: '2025-10-16T11:07:20+11:00',
      },
      {
        type: 'image',
        title: 'Pacific Outlook',
        caption: 'A wide-open Pacific view from the coastal trail.',
        src: mediaRef('adventures/sydney-2025-photo-1632.jpg'),
        alt: 'Pacific Ocean viewed from a green headland along the Sydney coast',
        aspectRatio: 0.75,
        capturedAt: '2025-10-16T11:27:20+11:00',
      },
      {
        type: 'image',
        title: 'Coastal Overlook',
        caption: 'One final look across the water before continuing the day.',
        src: mediaRef('adventures/sydney-2025-photo-1661.jpg'),
        alt: 'Sydney coastline and ocean viewed from a clifftop overlook',
        aspectRatio: 0.75,
        capturedAt: '2025-10-16T11:37:47+11:00',
      },
      {
        type: 'image',
        title: 'Reckon at Manly Beach',
        caption: 'A Reckon bottle marking the crew\'s afternoon at Manly Beach.',
        src: mediaRef('adventures/sydney-2025-img-5359.jpg'),
        alt: 'Reckon water bottle standing beside the ocean at Manly Beach',
        aspectRatio: 0.75,
        capturedAt: '2025-10-16T12:42:29+11:00',
      },
      {
        type: 'image',
        title: 'Featherdale Kangaroo',
        caption: 'A close encounter with one of Australia\'s most recognizable residents.',
        src: mediaRef('adventures/sydney-2025-od-photo-2915.jpg'),
        alt: 'A Reckon crew member crouching near a kangaroo at Featherdale Sydney Wildlife Park',
        aspectRatio: 0.75,
        capturedAt: '2025-10-17T09:30:00+11:00',
      },
      {
        type: 'image',
        title: 'Blue Mountains Crew',
        caption: 'The crew together with the Blue Mountains stretching into the distance.',
        src: mediaRef('adventures/sydney-2025-img-5583.jpg'),
        alt: 'Six Reckon crew members posing at a Blue Mountains overlook',
        aspectRatio: 1.33,
        capturedAt: '2025-10-17T12:34:50+11:00',
      },
      {
        type: 'image',
        title: 'Blue Mountains Overlook',
        caption: 'A group photo above the dramatic valleys of the Blue Mountains.',
        src: mediaRef('adventures/sydney-2025-img-5738.jpg'),
        alt: 'Six Reckon crew members at an overlook in the Blue Mountains',
        aspectRatio: 0.75,
        capturedAt: '2025-10-17T13:52:50+11:00',
      },
      {
        type: 'image',
        title: 'Rainforest Walk',
        caption: 'The crew paused together among the tree ferns and sandstone cliffs.',
        src: mediaRef('adventures/sydney-2025-img-5795.jpg'),
        alt: 'Reckon crew gathered on a rainforest walk in the Blue Mountains',
        aspectRatio: 0.75,
        capturedAt: '2025-10-17T15:01:12+11:00',
      },
      {
        type: 'image',
        title: 'Blue Mountains Panorama',
        caption: 'One more crew photo with the vast Blue Mountains landscape behind them.',
        src: mediaRef('adventures/sydney-2025-img-5809.jpg'),
        alt: 'Six Reckon crew members posing above a Blue Mountains valley',
        aspectRatio: 0.75,
        capturedAt: '2025-10-17T16:09:58+11:00',
      },
      {
        type: 'image',
        title: 'Featherdale Wombat',
        caption: 'A wombat encounter rounded out the crew\'s Australian wildlife experience.',
        src: mediaRef('adventures/sydney-2025-img-5949.jpg'),
        alt: 'A wombat resting at Featherdale Sydney Wildlife Park',
        aspectRatio: 0.75,
        capturedAt: '2025-10-17T19:39:33+11:00',
      },
    ],
  },
  {
    slug: 'sxsw-london-2025',
    legacySlugs: ['sxsw-london'],
    title: 'SXSW London 2025',
    subtitle: 'Technology. Film. Music. One Neighborhood at a Time.',
    meta: 'Conference',
    tripType: 'Conference',
    dateLabel: 'June 2–7, 2025',
    startDate: '2025-06-02',
    endDate: '2025-06-07',
    destination: {
      city: 'London',
      country: 'United Kingdom',
      neighborhood: 'Shoreditch',
    },
    conference: 'SXSW London',
    homeBase: 'Nobu London Shoreditch',
    crewCount: 2,
    familyFriendly: false,
    summary: 'A two-person conference trip through Shoreditch, where SXSW London spread across theaters, music venues, warehouses, pubs, and courtyards while the crew explored London as a future destination.',
    description: 'Every year there are hundreds of technology conferences around the world. We chose SXSW London because it promised something different.',
    purpose: 'Attend the inaugural SXSW London conference to explore emerging trends in AI, software development, film, and music while evaluating the event as a potential future Reckon Crew destination.',
    atAGlance: {
      destination: 'London, United Kingdom',
      dates: 'June 2–7, 2025',
      crewMembers: 2,
      conference: 'SXSW London',
      tripType: 'Conference',
      homeBase: 'Nobu London Shoreditch',
      timeZonesCrossed: 5,
      filmsPremiered: 2,
      liveMusicEvents: 4,
      breweriesVisited: 4,
      pubsVisited: '10+',
      familyFriendly: false,
    },
    attendees: tripAttendees(
      { employeeSlug: 'kevin-l', displayName: 'Kevin L.', role: 'CEO' },
      { employeeSlug: 'marcus-g', displayName: 'Marcus G.', role: 'Co-Founder' },
    ),
    crew: [
      {
        name: 'Kevin L.',
        role: 'CEO',
      },
      {
        name: 'Marcus G.',
        role: 'Co-Founder',
      },
    ],
    tags: tagRefs(
      'conference',
      'international',
      'united-kingdom',
      'london',
      'shoreditch',
      'ai',
      'software-development',
      'film',
      'live-music',
      'pubs',
      'historic-pubs',
      'craft-beer',
      'food',
      'fine-dining',
      'walking',
      'city-exploration',
      'hidden-gem',
      'bermondsey-beer-mile',
      'reckon-approved',
      'conference-series',
    ),
    achievements: achievementRefs(
      'bermondsey-beer-mile',
      'historic-pub-crawl',
    ),
    experienceDNA: {
      conference: 4,
      food: 4,
      adventure: 2,
      nightlife: 5,
      culture: 5,
      relaxation: 2,
      luxury: 3,
    },
    tripCard: {
      tripSlug: 'sxsw-london-2025',
      title: 'SXSW London 2025',
      subtitle: 'Technology. Film. Music. One Neighborhood at a Time.',
      destination: 'London, United Kingdom',
      date: 'June 2–7, 2025',
      tripType: 'Conference',
      heroImage: mediaRef('adventures/sxsw-london.png'),
      badgeIcon: 'UK',
      crewCount: 2,
      familyIncluded: false,
      featuredActivities: [
        'SXSW London',
        'AI and software sessions',
        'Film premieres',
        'Live music events',
        'Shoreditch walks',
      ],
      featuredMeals: [
        'London pubs',
        'Local breweries',
        'Shoreditch dinners',
      ],
      earnedBadges: [
        'Bermondsey Beer Mile',
        'Guinness Storehouse and Temple Bar',
      ],
      summary: 'A two-person conference trip through SXSW London, Shoreditch, live music, film, pubs, breweries, and the city experiences that could make London a future Reckon Crew destination.',
      ctaText: 'View Trip',
      ctaUrl: '/reckon-way/sxsw-london-2025',
    },
    coverImage: mediaRef('adventures/sxsw-london.png'),
    heroImage: mediaRef('adventures/sxsw-london-heroes-no-text.png'),
    inlineBlog: true,
    blog: sxswLondonBlog,
    media: [],
  },
  {
    slug: 'aws-reinvent-las-vegas',
    title: 'AWS re:Invent',
    subtitle: 'An annual Reckon tradition built around cloud technology, unforgettable dinners, and adventures beyond the conference floor.',
    meta: 'Recurring Conference',
    tripType: 'Conference',
    dateLabel: '2022 to 2025',
    destination: {
      city: 'Las Vegas',
      region: 'Nevada',
      country: 'United States',
    },
    description: 'Four years of AWS re:Invent, Reckon Crew growth, Las Vegas dining, and adventures beyond the conference floor.',
    tags: tagRefs(
      'conference',
      'annual-event',
      'adventure',
      'team-building',
      'city-exploration',
      'fine-dining',
      'steakhouse',
      'live-music',
      'hiking',
      'united-states',
      'conference-series',
      'best-restaurants',
    ),
    experienceDNA: {
      conference: 5,
      food: 3,
      nightlife: 4,
      culture: 2,
    },
    coverImage: mediaRef('adventures/aws-reinvent-las-vegas.png'),
    heroImage: mediaRef('adventures/aws-reinvent-heroes-no-text.png'),
    media: [],
  },
];

export const holidayPartyGalleries = [
  {
    slug: 'holiday-roses-luxury-2025',
    title: "Rose's Luxury 2025",
    meta: 'Holiday Party',
    description: "Good food, good friends, and Reckon holiday memories from Rose's Luxury.",
    tags: tagRefs('holiday-party', 'fine-dining', 'city-exploration', 'best-restaurants'),
    experienceDNA: {
      food: 5,
      family: 3,
      culture: 2,
      luxury: 4,
    },
    coverImage: mediaRef('adventures/holiday-roses-luxury-2025.png'),
    heroImage: mediaRef('adventures/holiday-roses-luxury-2025.png'),
    heroAspectRatio: 1.5,
    containHero: true,
    blog: createTravelBlog("Rose's Luxury 2025"),
    media: [],
  },
  {
    slug: 'holiday-inn-little-washington-2024',
    title: 'The Inn at Little Washington 2024',
    meta: 'Holiday Party',
    description: 'Snowy lights, warm drinks, and Reckon holiday memories from The Inn at Little Washington.',
    tags: tagRefs('holiday-party', 'fine-dining', 'best-restaurants', 'luxury'),
    experienceDNA: {
      food: 5,
      family: 3,
      relaxation: 3,
      luxury: 5,
    },
    coverImage: mediaRef('adventures/holiday-inn-little-washington-2024.png'),
    heroImage: mediaRef('adventures/holiday-inn-little-washington-2024.png'),
    heroAspectRatio: 1.5,
    containHero: true,
    blog: createTravelBlog('The Inn at Little Washington 2024'),
    media: [],
  },
  {
    slug: 'holiday-hells-kitchen-2023',
    title: "Hell's Kitchen at The Wharf 2023",
    meta: 'Holiday Party',
    description: "Waterfront lights, festive views, and Reckon holiday memories from Hell's Kitchen at The Wharf.",
    tags: tagRefs('holiday-party', 'fine-dining', 'city-exploration', 'best-restaurants'),
    experienceDNA: {
      food: 4,
      family: 3,
      nightlife: 4,
      culture: 2,
    },
    coverImage: mediaRef('adventures/holiday-hells-kitchen-2023.png'),
    heroImage: mediaRef('adventures/holiday-hells-kitchen-2023.png'),
    heroAspectRatio: 1.5,
    containHero: true,
    blog: createTravelBlog("Hell's Kitchen at The Wharf 2023"),
    media: [],
  },
  {
    slug: 'holiday-cut-dc-2022',
    title: 'Cut DC 2022',
    meta: 'Holiday Party',
    description: 'A polished dinner, festive table, and Reckon holiday memories from Cut DC.',
    tags: tagRefs('holiday-party', 'fine-dining', 'city-exploration', 'best-restaurants', 'luxury'),
    experienceDNA: {
      food: 5,
      family: 3,
      culture: 2,
      luxury: 5,
    },
    coverImage: mediaRef('adventures/holiday-cut-dc-2022.png'),
    heroImage: mediaRef('adventures/holiday-cut-dc-2022.png'),
    heroAspectRatio: 1.5,
    containHero: true,
    blog: createTravelBlog('Cut DC 2022'),
    media: [],
  },
];

export const allAdventureGalleries = [
  ...corporateRetreatGalleries,
  ...conferenceGalleries,
  ...holidayPartyGalleries,
];

// Add gallery items to the matching media array. For videos, use:
// { type: 'video', title: 'Clip title', src: mediaRef('adventures/file.mp4'), mimeType: 'video/mp4' }
export const galleryBySlug = allAdventureGalleries.reduce((galleryMap, gallery) => (
  [gallery.slug, ...(gallery.legacySlugs || [])].reduce((slugMap, slug) => ({
    ...slugMap,
    [slug]: gallery,
  }), galleryMap)
), {});

export const getGalleryBlogPosts = (gallery) => {
  if (!gallery) {
    return [];
  }

  return gallery.blogPosts || (gallery.blog ? [gallery.blog] : []);
};

export const slugifyStory = (value) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

export const getBlogPostSlug = (post) => post.slug || slugifyStory(post.title);

export const getBlogPostBySlug = (gallery, storySlug) => {
  const posts = getGalleryBlogPosts(gallery);
  const index = posts.findIndex((post) => (
    getBlogPostSlug(post) === storySlug
    || (post.legacySlugs || []).includes(storySlug)
  ));

  if (index < 0) {
    return {
      post: null,
      index: -1,
      posts,
    };
  }

  return {
    post: posts[index],
    index,
    posts,
  };
};
